import {hpcAttrs} from '@github-ui/hpc'
import {EstablishedAhpSessionProvider} from '@github-ui/copilot-shared-ui/components/EstablishedAhpSessionProvider'
import {useCopilotResultPresentationSelection} from '@github-ui/copilot-browser-latency/use-copilot-result-presentation'
import {useReportUserVisibleError} from '@github-ui/copilot-browser-latency/use-report-user-visible-error'
import {
  recordTaskRepositoryScope,
  repositoryScopeFromRepositoryId,
} from '@github-ui/copilot-browser-latency/repository-scope'
import {recordInitialTaskStartType, startTypeFromEnvironment} from '@github-ui/copilot-browser-latency/start-type'
import type {
  AhpSpaceSelection,
  ContributedInstructionMetadata,
  ContributedInstructionsMetadata,
  DirectTurnSurface,
  DirectTurnTelemetryContext,
  EstablishedAhpSession,
  EstablishedAhpSessionBootstrap,
  EstablishedAhpResumptionAttempt,
  PreparedPrompt,
} from '@github-ui/ahp-session-client/contracts'
import {createEstablishedAhpResumptionDeadline} from '@github-ui/ahp-session-client/resumption-deadline'
import type {ChatInputRef} from '@github-ui/copilot-shared-ui/components/chat-input-ref'
import {useNavigate, useSearchParams} from '@github-ui/use-navigate'
import {Layout as PacerLayout} from '@github-ui/pacer/Layout'
import {Banner, Spinner, IconButton, useMergedRefs} from '@primer/react'
import {
  use,
  useCallback,
  useEffect,
  useEffectEvent,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  useSyncExternalStore,
  type ComponentProps,
  type RefObject,
} from 'react'
import {addUrlToHistoryStack} from '@github-ui/history'
import {SessionContextProvider} from '../../contexts/SessionContext'
import {useSessions} from '../../hooks/useSessions'
import {PLAN_STATE_APPROVED, PLAN_STATE_DISMISSED} from '../../hooks/use-patch-session'
import {usePendingPlanGate} from '../../hooks/use-pending-plan-gate'
import type {AgentTaskAppPayload} from '../../types/agent-task-app-payload'
import {extractSpecTaskThreadIdFromEventIdentifiers} from '../../utils/extract-spec-task-thread-id'
import {COPILOT_CHAT_SPEC_TASK_EVENT_TYPE, getAgentsPath} from '../../utils/constants'
import {
  isActiveSession,
  isPendingSession,
  SessionState,
  TERMINAL_SESSION_STATES,
  type Session as SessionType,
} from '../../types/session'
import {isCCRAgent, isReadonlyAgent} from '@github-ui/copilot-shared-ui/agents/agent-helpers'
import {
  CCA_PLAN_ACTION_IMPLEMENT,
  CCA_PLAN_ACTION_EXIT,
  CCA_PLAN_ACTIONS,
  CopilotFinalStopMessage,
} from './messages/CopilotFinalStopMessage'
import {UserInitialMessage} from './messages/UserInitialMessage'
import type {SessionEnvironment} from './messages/SessionMetadataRow'
import {Session} from './Session'
import {SessionMetadataBar} from './SessionFooter'
import {PlanApprovalDisplay} from '../session-logs/PlanApprovalDisplay'
import styles from './TaskChat.module.css'
import {useAgentMentions} from '../../hooks/useAgentMentions'
import {useTaskAuthor} from '../../hooks/use-task-author'
import {UserMessage} from './messages/UserMessage'
import {UserProblemStatementMessage} from './messages/UserProblemStatementMessage'
import type {AgentMention} from '../../types/agent-mention'
import {usePendingSession} from '../../hooks/usePendingSession'
import {useInactiveLogs} from '../../hooks/useInactiveLogs'
import {CopilotReplyMessage} from './messages/CopilotReplyMessage'
import {useCreateAPISession} from '../../hooks/use-create-api-session'
import {AlertIcon, ArrowDownIcon} from '@primer/octicons-react'
import {resolveAgentProfile, resolveAgentProfileForAPITask} from '../../agents/resolve-agent-profile'
import {useIsAhpSession} from '../../hooks/use-is-ahp-session'
import {agentEnvironmentQueryKey, isEnvironmentStatusEnabled} from '../../hooks/use-environment'
import type {Environment, EnvironmentKind} from '@github-ui/agent-sessions-types/environment'
import {getQueryClient} from '@github-ui/query-client'
import {useAwaitingIntentWake} from '../../hooks/use-awaiting-intent-wake'
import {useDeferredSandboxConnection} from '../../hooks/use-deferred-sandbox-connection'
import {useTaskAhpHistory} from '../../hooks/use-task-ahp-history'
import {hasPendingAhpUserPrompt} from '../../utils/has-pending-ahp-user-prompt'
import {SavedTaskChangesetContext, useHasSessionChangeset} from '../../hooks/use-task-changeset'
import {findSavedChangesetUri} from '../../utils/find-session-changeset-uri'
import {
  useConnectionAttemptPresentation,
  useEstablishedAhpSession as useMissionControlEstablishedAhpSession,
} from '../../hooks/use-established-ahp-session'
import {useEstablishedAhpResumptionAttemptError} from '../../hooks/use-established-ahp-resumption-attempt-error'
import {
  persistedFirstTurnPrompt,
  useAhpFirstTurnRecovery,
  useFirstTurnRecoverySelection,
} from '../../hooks/use-ahp-first-turn-recovery'
import {Blankslate} from '@primer/react/experimental'
import {useAutoScroll} from '@github-ui/copilot-shared-ui/hooks/use-auto-scroll'
import {useDiffEventHandler} from '../../hooks/useDiffEventHandler'
import {CurrentUserProvider, useCurrentUser} from '../../contexts/CurrentUserContext'
import {TaskChatHeader} from './TaskChatHeader'
import {StickyTaskChatHeader} from './StickyTaskChatHeader'
import {AgentsWorkbenchHeader} from '../workbench/AgentsWorkbenchHeader'
import {AgentsWorkbenchContext} from '../workbench/AgentsWorkbenchContext'
import {TaskSidebarFallbackToggle} from './TaskSidebarFallbackToggle'
import {TaskChatInputV2} from './TaskChatInputV2'
import {EstablishedTaskChatInput} from './EstablishedTaskChatInput'
import {NewTaskContext} from '../../contexts/new-task-context'
import {ConversationAccessContext, ConversationLockedFocusContext} from '../../contexts/ConversationAccessContext'
import {CONVERSATION_LOCKED_ANNOUNCEMENT} from '../conversation-access/ConversationLocked'
import {SessionResumeNotice} from './SessionResumeNotice'
import {QuickChatProvisioningUserMessage} from './QuickChatProvisioningUserMessage'
import {LegacyChatInitialMessageContext} from './legacy-chat-initial-message-context'
import {DiffOverlay} from './DiffOverlay'
import {useOpenDiffWorkbenchTab} from '../../hooks/use-open-diff-workbench-tab'
import {AhpTaskChatInputActions} from './AhpTaskChatInputActions'
import type {TaskHistorySession} from '../../utils/replay-task-history-events'
import {baseRefMatchesHeadRef, getBranchArtifactData, getGitHubArtifactData} from '../../utils/task-artifact-helpers'
import {clsx} from 'clsx'
import {usePullRequest} from '../../hooks/usePullRequest'
import {useTask} from '../../hooks/use-task'
import {useTaskAliveUpdates} from '../../hooks/use-task-alive-updates'
import {SESSION_LOGS_QUERY_KEY} from '../../hooks/useLogs'
import {generateEventsQueryKey} from '../../utils/fetch-session-events'
import type {HydratedAPITask} from '../../types/api-task'
import {shouldShowTimelineVerbosityControl, TimelineVerbosityProvider} from '../session-logs/ahp/timeline-verbosity'
import {isFeatureEnabled, isFeatureEnabledWithOptOut} from '@github-ui/feature-flags'
import {ConsecutiveCAPI401sProvider} from '@github-ui/copilot-shared-ui/capi/ConsecutiveCAPI401sContext'
import {
  useContentPreview,
  useContentPreviewOpen,
  // eslint-disable-next-line @github-ui/github-monorepo/restrict-relay-imports
} from '@github-ui/copilot-immersive-v1/components/ContentPreview/ContentPreviewContext'
import {CopilotNavigationWithContext} from '@github-ui/copilot-chat/components/navigation/CopilotNavigation'
import {CurrentRepositoryProvider, type Repository} from '@github-ui/current-repository'
import {TaskActionProviders} from '../task-list/TaskActionProviders'
import {ArchiveErrorBanner} from '../ArchiveErrorBanner'
import {EnvironmentTaskLaunchFailureBanner} from './EnvironmentTaskLaunchFailureBanner'
import {ChatContinuationUnavailable} from '@github-ui/copilot-shared-ui/components/ChatContinuationUnavailable'
import {getChatContinuationEligibility} from '@github-ui/copilot-shared-ui/utils/chat-continuation-eligibility'
import {isSandboxComputeProvider} from '../../utils/effective-remote-control-policy'
import {SidebarAgentFilterProvider} from '../../contexts/SidebarAgentFilterContext'
import {useOrFetchTaskPayload} from '../../hooks/useOrFetchTask'
import {
  SessionLoadingBoundary,
  useSessionLoadingPresentationOwnsFallback,
  type SessionLoadingSource,
} from '../../hooks/use-session-loading-presentation'
import {SessionLoadingIndicator} from '../session-loading/SessionLoadingIndicator'
import {createCopilotChatTurnLatencyTracker} from '../../ahp/turn-latency'

// eslint-disable-next-line @github-ui/github-monorepo/restrict-relay-imports
import {ContentPreviewSlot} from '@github-ui/copilot-immersive-v1/components/ContentPreview/ContentPreviewComponentContext'
import {useNavigableRef} from '@github-ui/navigable-ref'
import {useViewMode} from '../../contexts/ViewModeContext'
import {setTitle} from '@github-ui/document-metadata'
import {OptimisticLogsProvider} from '../../contexts/OptimisticLogsContext'
import {AgentProfileProvider} from '../../contexts/AgentProfileContext'
import {useLatestAgentModel} from '@github-ui/one-copilot-composer/use-latest-agent-model'
import {useLatestReasoningEffort} from '@github-ui/one-copilot-composer/use-latest-reasoning-effort'
import {reconcileSessionAiCredits} from '../../utils/session-usage'
import {announce} from '@github-ui/aria-live'
import {canSteerMessagingAppDm, getChannelSessionOrigin, isChannelSession} from '../../utils/channel-session'
import {getTurns} from '@github-ui/ahp-relay/ahp-session-selectors'
import {ModelChangeStatusEntry} from '../session-logs/ahp/ModelChangeStatusEntry'
import type {ModelChangeMarker} from '../session-logs/ahp/model-change-marker'
import type {TimelineMarker} from '../session-logs/ahp/AhpTimelineView'
import type {ReasoningEffort} from '@github-ui/copilot-shared-ui/utils/reasoning-effort'
import {
  captureEnvironmentTaskLaunchTaskResolver,
  type EnvironmentTaskLaunchTaskResolver,
} from '../../hooks/use-environment-task-launch'
import {isRepolessChatTask} from '../../utils/repoless-chat-task'
import {useTaskStartupTimeline} from '../session-logs/ahp/use-task-startup-timeline'
import {AppliedInstructionsDialog} from '@github-ui/copilot-chat/components/AppliedInstructionsDialog'
import {PersonalInstructionsDialog} from '@github-ui/copilot-chat/components/PersonalInstructionsDialog'
import {buildAhpRecoveryCreateSession} from './ahp-session-recovery'
import {
  useHasCopilotChatManager,
  useOptionalContributedInstructionSnapshot as useLegacyContributedInstructionSnapshot,
} from '@github-ui/copilot-chat/CopilotChatManagerContext'
import {
  useHasContributedInstructionsProvider,
  useContributedInstructionsReady,
  useOptionalContributedInstructionSnapshot,
  useOptionalPersonalInstructionsEditor,
} from '@github-ui/copilot-shared-ui/contexts/ContributedInstructionsContext'

export function resolveDirectTurnTelemetryContext({
  surface,
  environmentKind,
  taskId,
  taskData,
  session,
}: {
  surface?: DirectTurnSurface
  environmentKind?: string
  taskId?: string
  taskData?: Pick<HydratedAPITask, 'repository'>
  session?: Pick<EstablishedAhpSession, 'identity'>
}): DirectTurnTelemetryContext | undefined {
  if (!surface || environmentKind !== 'managed-sandbox' || !taskId || !taskData || !session) return undefined

  return {
    surface,
    environmentId: session.identity.environmentId,
    taskId,
    sessionId: session.identity.sessionId,
    repositoryBacked: taskData.repository.id > 0,
  }
}

const subscribeToNothing = () => () => undefined
const getAbsentEstablishedSessionLifecycle = () => 'disposed' as const

function TaskChatSessionProvider({
  saved,
  ...props
}: ComponentProps<typeof EstablishedAhpSessionProvider> & {saved: TaskHistorySession | undefined}) {
  return (
    <SavedTaskChangesetContext value={saved}>
      <EstablishedAhpSessionProvider {...props} />
    </SavedTaskChangesetContext>
  )
}

function getPageLoadingSources({
  lookup,
  preparation,
  connection,
}: {
  lookup: {pending: boolean; error: boolean}
  preparation: {
    pending: boolean
    error: boolean
    phase?: 'preparing' | 'cloning'
    progress?: number
    launchKind?: EnvironmentKind
  }
  connection: {
    pending: boolean
    error: boolean
    replacesLoading: boolean
    phase?: Extract<SessionLoadingSource, {kind: 'connection'}>['phase']
  }
}): SessionLoadingSource[] {
  return [
    lookup.error
      ? {kind: 'lookup', state: 'error', replacesLoading: true}
      : {kind: 'lookup', state: lookup.pending ? 'pending' : 'idle'},
    preparation.error
      ? {kind: 'preparation', state: 'error', replacesLoading: true}
      : {
          kind: 'preparation',
          state: preparation.pending ? 'pending' : 'idle',
          phase: preparation.phase,
          progress: preparation.progress,
          launchKind: preparation.launchKind,
        },
    connection.error
      ? {kind: 'connection', state: 'error', replacesLoading: connection.replacesLoading}
      : {
          kind: 'connection',
          state: connection.pending ? 'pending' : 'idle',
          phase: connection.phase,
        },
  ]
}

// Encapsulates diff overlay state, refs, and callbacks used by TaskChat.
function useDiffOverlay({
  artifacts,
  pullBaseRef,
  pullHeadRef,
  hasActiveSession,
}: {
  artifacts: HydratedAPITask['artifacts']
  pullBaseRef: string | undefined
  pullHeadRef: string | undefined
  hasActiveSession: boolean
}) {
  const [isOpen, setIsOpen] = useState(false)
  const [initialFilePath, setInitialFilePath] = useState<string | null>(null)

  const branchArtifact = getBranchArtifactData(artifacts)
  const baseRef = (!baseRefMatchesHeadRef(branchArtifact) && branchArtifact?.base_ref) || pullBaseRef
  const headRef = branchArtifact?.head_ref || pullHeadRef

  // Close the overlay when a session becomes active
  const [prevHasActiveSession, setPrevHasActiveSession] = useState(hasActiveSession)
  if (prevHasActiveSession !== hasActiveSession) {
    setPrevHasActiveSession(hasActiveSession)
    if (!prevHasActiveSession && hasActiveSession) {
      setIsOpen(false)
      setInitialFilePath(null)
    }
  }

  const close = useCallback(() => {
    setIsOpen(false)
    setInitialFilePath(null)
  }, [])

  const onViewDiff = useMemo(
    () => (filePath?: string) => {
      if (isOpen && !filePath) {
        setIsOpen(false)
        setInitialFilePath(null)
      } else {
        setInitialFilePath(filePath ?? null)
        setIsOpen(true)
      }
    },
    [isOpen],
  )

  const onCloseDiff = useMemo(() => close, [close])

  return {isOpen, initialFilePath, baseRef, headRef, close, onViewDiff, onCloseDiff}
}

/** Clamps `ref`'s max-height to the viewport, recalculated on scroll/resize, so the diff overlay stays bounded without switching the page's own scroll target. */
function useClampToViewport(ref: RefObject<HTMLElement | null>, enabled: boolean) {
  useLayoutEffect(() => {
    if (!enabled) return
    const el = ref.current
    if (!el) return

    let rafId = 0
    const measure = () => {
      rafId = 0
      el.style.maxHeight = `${Math.max(0, window.innerHeight - el.getBoundingClientRect().top)}px`
    }
    const scheduleMeasure = () => {
      if (!rafId) rafId = requestAnimationFrame(measure)
    }

    // Override `.documentScrollRoot`'s `overflow: visible` so max-height actually clips.
    el.style.overflow = 'hidden'
    measure()
    // eslint-disable-next-line github/prefer-observers -- needs the element's live viewport offset
    window.addEventListener('scroll', scheduleMeasure)
    // eslint-disable-next-line github/prefer-observers -- viewport size, not element intersection
    window.addEventListener('resize', scheduleMeasure)

    return () => {
      window.removeEventListener('scroll', scheduleMeasure)
      window.removeEventListener('resize', scheduleMeasure)
      if (rafId) cancelAnimationFrame(rafId)
      el.style.maxHeight = ''
      el.style.overflow = ''
    }
  }, [ref, enabled])
}

interface TaskChatSuspendedWrapperPayload extends AgentTaskAppPayload {
  repository?: Repository
}

// Wrapper for showing the repo-agents style page, but under Copilot Immersive
export function TaskChatSuspendedWrapper() {
  return <TaskChatSuspendedWrapperInner />
}

function PageLoadingSession(props: ComponentProps<typeof Session>) {
  const presentationOwnsFallback = useSessionLoadingPresentationOwnsFallback()
  return <Session {...props} suppressLocalLifecycleLoading={presentationOwnsFallback} />
}

function TaskChatSuspendedWrapperInner() {
  const {data: payload} = useOrFetchTaskPayload()

  const ref = useNavigableRef<HTMLTextAreaElement>()

  const isContentPreviewOpen = useContentPreviewOpen()
  const {openPreviewPane, closePreviewPane} = useContentPreview()

  const {currentUser, repository} = (payload || {}) as TaskChatSuspendedWrapperPayload

  // Check if the current user is a session owner by matching their ID against session user_ids.
  const currentUserIsSessionOwner = !!currentUser && !!payload?.sessions?.some(s => s.user_id === currentUser.id)

  const setContentPreviewPaneOpen = useCallback(
    (open: boolean) => {
      if (open) {
        openPreviewPane()
      } else {
        closePreviewPane()
      }
    },
    [openPreviewPane, closePreviewPane],
  )

  return (
    <SidebarAgentFilterProvider>
      <ConsecutiveCAPI401sProvider>
        <PacerLayout
          mainColumn="left"
          {...hpcAttrs()}
          secondaryColumnState={[isContentPreviewOpen, setContentPreviewPaneOpen]}
          slimLineTopTabs
        >
          <PacerLayout.Sidebar>
            <CopilotNavigationWithContext />
          </PacerLayout.Sidebar>
          <PacerLayout.ColumnLeft>
            {!payload && (
              <div className={styles.suspenseSpinner}>
                <Spinner size="medium" />
              </div>
            )}
            {payload && repository && (
              <CurrentRepositoryProvider repository={repository}>
                <CurrentUserProvider
                  currentUser={payload.currentUser}
                  currentUserIsSessionOwner={currentUserIsSessionOwner}
                >
                  <TaskChat
                    key={payload.taskId}
                    directTurnTelemetrySurface="global_task"
                    repoOwner={repository.ownerLogin}
                    repoName={repository.name}
                    showHeader
                    navigateOnDelete
                    {...payload}
                  />
                </CurrentUserProvider>
              </CurrentRepositoryProvider>
            )}
            {payload && !repository && (
              <CurrentUserProvider
                currentUser={payload.currentUser}
                currentUserIsSessionOwner={currentUserIsSessionOwner}
              >
                <TaskChat
                  key={payload.taskId}
                  directTurnTelemetrySurface="global_task"
                  showHeader
                  navigateOnDelete
                  {...payload}
                />
              </CurrentUserProvider>
            )}
          </PacerLayout.ColumnLeft>
          <PacerLayout.ColumnRight>
            <ContentPreviewSlot returnFocusRef={ref} />
          </PacerLayout.ColumnRight>
        </PacerLayout>
      </ConsecutiveCAPI401sProvider>
    </SidebarAgentFilterProvider>
  )
}

export function CenteredSpinner() {
  return (
    <div className={styles.blankSlateContainer} style={{alignItems: 'center'}}>
      <Spinner />
    </div>
  )
}

export function CenteredBlankSlate({
  title,
  description,
  statusLink,
  pullLink,
}: {
  title: string
  description: string
  statusLink?: string
  pullLink?: string
}) {
  return (
    <div className={styles.blankSlateContainer}>
      <Blankslate>
        <Blankslate.Visual>
          <AlertIcon size={24} className="fgColor-muted" />
        </Blankslate.Visual>
        <Blankslate.Heading>{title}</Blankslate.Heading>
        <Blankslate.Description className="text-center">{description}</Blankslate.Description>
        {statusLink ? <Blankslate.SecondaryAction href={statusLink}>GitHub status</Blankslate.SecondaryAction> : null}
        {pullLink ? <Blankslate.SecondaryAction href={pullLink}>Go to pull request</Blankslate.SecondaryAction> : null}
      </Blankslate>
    </div>
  )
}

interface TaskChatProps extends AgentTaskAppPayload {
  directTurnTelemetrySurface?: DirectTurnSurface
  showHeader?: boolean
  useSidebarPageLayoutHeader?: boolean
  repoOwner?: string
  repoName?: string
  taskId?: string
  branchAliveChannel?: string
  task?: HydratedAPITask
  scrollTarget?: 'container' | 'document'
  hideArchive?: boolean
  hideActions?: boolean
  hideEnvironmentVisual?: boolean
  cliRemoteNoticeDismissed?: boolean
  navigateOnDelete?: boolean
  deletionDestination?: string
  establishedAhpSession?: EstablishedAhpSession
  establishedAhpSessionPending?: boolean
  establishedAhpBootstrap?: EstablishedAhpSessionBootstrap
  requiresEstablishedAhpBootstrap?: boolean
  announceEstablishedAhpSessionPending?: boolean
  onDeleteSuccess?: (taskId: string) => void
  chatApiUrl?: string
  restoredPrompt?: string
  onRestoredPromptChange?: (prompt: string) => void
  onRestoredPromptAccepted?: (prompt: string) => void
  onUserRename?: (title: string) => void
  onTaskNameChange?: (name: string | undefined) => void
  hideChatInput?: boolean
  onTaskStateChange?: (taskId: string) => void
  profileSlugOverride?: string
  requiredBootstrapError?: string
  onEnvironmentTaskLaunchAction?: () => void
  environmentTaskLaunchActionLabel?: string
  provisioningPrompt?: PreparedPrompt
  createdInCurrentView?: boolean
  provisionalLaunchKind?: EnvironmentKind
  /**
   * Whether this task is being provisioned from the current view. The restored prompt,
   * provisioning draft, and initial space props are ignored unless this is true.
   */
  isProvisioning?: boolean
  initialSelectedSpace?: AhpSpaceSelection
  provisioningPromptMode?: 'until-transcript'
  provisioningDraft?: string
  onProvisioningDraftChange?: (draft: string) => void
  onEstablishedAhpSession?: (session: EstablishedAhpSession) => void
  sessionLoadingMessage?: string
}

function shouldPreserveInitialTaskName(initialTaskName: string | undefined, fetchedTaskName: string | undefined) {
  if (!initialTaskName || initialTaskName === fetchedTaskName) return false
  const isInitialTaskNamePlaceholder =
    initialTaskName === 'New chat session' || initialTaskName === 'New remote session'
  if (isInitialTaskNamePlaceholder) return false
  return !fetchedTaskName || fetchedTaskName === 'New chat session' || fetchedTaskName === 'New remote session'
}

const ScrollToBottomButton = ({isAtBottom, onScrollToBottom}: {isAtBottom: boolean; onScrollToBottom: () => void}) => {
  if (isAtBottom) return null

  return (
    <IconButton
      type="button"
      aria-label="Scroll to bottom"
      className={styles.scrollToBottomButton}
      onClick={onScrollToBottom}
      icon={ArrowDownIcon}
      tooltipDirection="n"
    />
  )
}

export function TaskChat({
  directTurnTelemetrySurface,
  showHeader = false,
  useSidebarPageLayoutHeader = false,
  repoOwner,
  repoName,
  taskId,
  branchAliveChannel,
  task,
  scrollTarget = 'container',
  hideArchive = false,
  hideActions = false,
  hideEnvironmentVisual = false,
  cliRemoteNoticeDismissed,
  navigateOnDelete,
  deletionDestination,
  establishedAhpSession,
  establishedAhpSessionPending,
  establishedAhpBootstrap,
  requiresEstablishedAhpBootstrap,
  announceEstablishedAhpSessionPending,
  onDeleteSuccess,
  chatApiUrl,
  restoredPrompt: restoredPromptInput,
  onRestoredPromptChange: onRestoredPromptChangeInput,
  onRestoredPromptAccepted: onRestoredPromptAcceptedInput,
  onUserRename,
  onTaskNameChange,
  hideChatInput,
  onTaskStateChange,
  profileSlugOverride,
  requiredBootstrapError,
  onEnvironmentTaskLaunchAction,
  environmentTaskLaunchActionLabel,
  provisioningPrompt,
  createdInCurrentView = false,
  provisionalLaunchKind,
  isProvisioning = false,
  initialSelectedSpace: initialSelectedSpaceInput,
  provisioningPromptMode,
  provisioningDraft: provisioningDraftInput,
  onProvisioningDraftChange: onProvisioningDraftChangeInput,
  onEstablishedAhpSession,
  sessionLoadingMessage,
  ...payload
}: TaskChatProps) {
  const initialSelectedSpace = isProvisioning ? initialSelectedSpaceInput : undefined
  const provisioningDraft = isProvisioning ? provisioningDraftInput : undefined
  const onProvisioningDraftChange = isProvisioning ? onProvisioningDraftChangeInput : undefined
  const restoredPrompt = isProvisioning ? restoredPromptInput : undefined
  const onRestoredPromptChange = isProvisioning ? onRestoredPromptChangeInput : undefined
  const onRestoredPromptAccepted = isProvisioning ? onRestoredPromptAcceptedInput : undefined
  const {
    agentMentions,
    pull,
    sessions,
    logsPollingInterval = 3000,
    sessionsPollingInterval = 3000,
    remoteControlPolicyEnabled,
    initialAuthToken,
    taskAliveChannel,
  } = payload

  const effectiveTaskId = taskId ?? pull?.global_relay_id
  // Transcripts on this page that were refused (the viewer can no longer read
  // something the conversation reached), keyed by reporter. Any refusal in the
  // current task withdraws the input. Reset when the page moves to another task.
  const [refusedTaskIds, setRefusedTaskIds] = useState<{taskId: typeof effectiveTaskId; ids: ReadonlySet<string>}>(
    () => ({taskId: effectiveTaskId, ids: new Set()}),
  )
  if (refusedTaskIds.taskId !== effectiveTaskId) setRefusedTaskIds({taskId: effectiveTaskId, ids: new Set()})
  const conversationRefused = refusedTaskIds.taskId === effectiveTaskId && refusedTaskIds.ids.size > 0
  const reportConversationRefused = useCallback((reporterId: string, refused: boolean) => {
    setRefusedTaskIds(current => {
      if (current.ids.has(reporterId) === refused) return current
      const ids = new Set(current.ids)
      if (refused) ids.add(reporterId)
      else ids.delete(reporterId)
      return {...current, ids}
    })
  }, [])
  const {taskStartup, cloneProgress, provisioning} = useTaskStartupTimeline(effectiveTaskId)
  const creatingTask = createdInCurrentView || !!taskStartup || !!provisioningPrompt
  const [creation, setCreation] = useState({taskId: effectiveTaskId, started: creatingTask})
  const startedHere = creatingTask || (creation.taskId === effectiveTaskId && creation.started)
  if (creation.taskId !== effectiveTaskId || creation.started !== startedHere) {
    setCreation({taskId: effectiveTaskId, started: startedHere})
  }
  const repoNwo = useMemo(
    () => pull?.repository_nwo || (repoOwner && repoName ? `${repoOwner}/${repoName}` : ''),
    [pull?.repository_nwo, repoName, repoOwner],
  )
  const viewMode = useViewMode()
  const isSidePanel = viewMode === 'sidePanel'
  // The workbench sizes itself to the viewport and gives the preview column its own
  // scroll container, so the timeline must scroll inside the chat column rather than
  // handing scrolling to the document (which would also unpin the composer).
  const isInWorkbench = use(AgentsWorkbenchContext)
  const usesDocumentScroll = scrollTarget === 'document' && !isSidePanel && !isInWorkbench

  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const urlSessionId = searchParams.get('session_id')
  const {currentUser, currentUserIsSessionOwner} = useCurrentUser()
  const [highlightedSessionId, setHighlightedSessionId] = useState<string | null>(urlSessionId)
  const [contributedInstruction, setContributedInstruction] = useState<ContributedInstructionMetadata>()
  const [isPersonalInstructionsDialogOpen, setIsPersonalInstructionsDialogOpen] = useState(false)
  // Prevents multiple removals of session_id from the URL for the same urlSessionId.
  const sessionIdClearedRef = useRef(false)
  const appliedInstructionsTriggerRef = useRef<HTMLElement | null>(null)
  const hasCopilotChatManager = useHasCopilotChatManager()
  const hasContributedInstructionsProvider = useHasContributedInstructionsProvider()
  const instructionsReady = useContributedInstructionsReady()
  const personalInstructionsEditor = useOptionalPersonalInstructionsEditor()
  const contributedInstructionSnapshot = useOptionalContributedInstructionSnapshot()
  const legacyContributedInstructionSnapshot = useLegacyContributedInstructionSnapshot()
  const currentInstructions = hasContributedInstructionsProvider
    ? contributedInstructionSnapshot
    : legacyContributedInstructionSnapshot
  const openAppliedInstructions = useCallback(
    (instructions: ContributedInstructionsMetadata, trigger: HTMLElement) => {
      appliedInstructionsTriggerRef.current = trigger
      const instruction = instructions[0]
      if (instruction?.kind === 'personal' && (hasCopilotChatManager || personalInstructionsEditor)) {
        setContributedInstruction(undefined)
        setIsPersonalInstructionsDialogOpen(true)
      } else {
        setIsPersonalInstructionsDialogOpen(false)
        setContributedInstruction(instruction)
      }
    },
    [hasCopilotChatManager, personalInstructionsEditor],
  )
  const closeAppliedInstructions = useCallback(() => setContributedInstruction(undefined), [])
  const closePersonalInstructions = useCallback(() => setIsPersonalInstructionsDialogOpen(false), [])

  const scrollContainerRef = useRef<HTMLDivElement>(null)
  const chatInputRef = useRef<ChatInputRef>(null)
  const textAreaRef = useRef<HTMLTextAreaElement>(null)
  const environmentTaskLaunchTaskResolverRef = useRef<
    | {
        taskId: string
        resolve: EnvironmentTaskLaunchTaskResolver
      }
    | undefined
  >(undefined)
  const [selectedModelId] = useLatestAgentModel()
  const [selectedReasoningEffort] = useLatestReasoningEffort()
  const pendingTaskStartupPrompt = taskStartup?.status === 'pending' ? taskStartup.prompt : undefined
  const [modelChangeState, setModelChangeState] = useState<{
    sessionKey?: string
    markers: ModelChangeMarker[]
  }>({markers: []})
  const markerSequence = useRef(0)

  const {
    data: fetchedTaskData,
    dataUpdatedAt: taskDataUpdatedAt,
    isError: taskDataIsError,
    refetch: refetchTask,
  } = useTask({
    owner: repoOwner,
    repo: repoName,
    taskId,
    initialTask: task,
  })
  const taskData = useMemo(() => {
    if (!fetchedTaskData) return fetchedTaskData
    return shouldPreserveInitialTaskName(task?.name, fetchedTaskData.name)
      ? {...fetchedTaskData, name: task?.name}
      : fetchedTaskData
  }, [fetchedTaskData, task?.name])

  const scopedTaskId = taskData?.id
  const taskRepositoryId = taskData?.repository?.id
  // Layout timing records the scope before readiness can observe the composer as input-ready.
  useLayoutEffect(() => {
    recordTaskRepositoryScope(scopedTaskId, repositoryScopeFromRepositoryId(taskRepositoryId))
  }, [scopedTaskId, taskRepositoryId])

  useEffect(() => {
    if (!effectiveTaskId) return
    const capturedResolve = captureEnvironmentTaskLaunchTaskResolver(effectiveTaskId)
    environmentTaskLaunchTaskResolverRef.current = capturedResolve
      ? {taskId: effectiveTaskId, resolve: capturedResolve}
      : undefined
    const resolve = environmentTaskLaunchTaskResolverRef.current?.resolve
    if (!resolve) return
    if (taskData) {
      resolve(taskData)
    } else if (taskDataIsError) {
      resolve(null)
    }
  }, [
    effectiveTaskId,
    establishedAhpSession,
    establishedAhpSessionPending,
    requiredBootstrapError,
    taskData,
    taskDataIsError,
  ])

  // Keep the browser tab title in sync with the task being viewed (not the last
  // session that happened to set it). Skip in side panels, where the host page
  // owns the document title. Gated on the sticky-header flag so the flag-off
  // path keeps the server-rendered title unchanged.
  useEffect(() => {
    if (isSidePanel) return
    if (onTaskNameChange) {
      onTaskNameChange(taskData?.name)
      return
    }
    if (!isFeatureEnabled('copilot_mission_control_sticky_header')) return
    setTitle(`${taskData?.name ?? 'New task'} · GitHub Copilot`)
  }, [taskData?.name, isSidePanel, onTaskNameChange])

  const {createSession: createNewSession} = useCreateAPISession()

  const handlePlanApprove = (session: SessionType) => (action: string, planSummary: string) => {
    if (!effectiveTaskId) return
    if (action === CCA_PLAN_ACTION_EXIT) {
      updatePlanState(session.id, PLAN_STATE_DISMISSED)
      // Use setTimeout to ensure the textarea is ready to receive focus after React re-renders
      setTimeout(() => {
        textAreaRef.current?.focus()
      }, 0)
      return
    }
    updatePlanState(session.id, PLAN_STATE_APPROVED)
    const prompt = action === CCA_PLAN_ACTION_IMPLEMENT ? `Implement the plan:\n\n${planSummary}` : action
    createNewSession({
      nwo: repoNwo,
      taskId: effectiveTaskId,
      problemStatement: prompt,
      model: selectedModelId,
      reasoningEffort: selectedReasoningEffort,
      eventType: 'agent_task_post_session_steering',
    })
  }

  const agentProfile = profileSlugOverride
    ? resolveAgentProfile(profileSlugOverride)
    : resolveAgentProfileForAPITask(taskData)

  // When the agent creates a PR, the task artifacts will contain a github_resource artifact.
  // Use usePullRequest to reactively fetch the PR data without a full page reload.
  const taskPullRequestId = taskData ? getGitHubArtifactData(taskData.artifacts)?.id : undefined
  const {data: detectedPull} = usePullRequest({
    pullRequestId: !pull ? taskPullRequestId : undefined,
    includeBody: false,
    refetchOnMount: true,
  })

  // Use the detected PR if the original payload didn't include one
  const effectivePull = pull ?? detectedPull
  const effectivePayload = {...payload, pull: effectivePull}

  const {
    data: sessionsData,
    isError: sessionsIsError,
    timedOut: sessionsTimedOut,
    isFetchedAfterMount: notUsingSessionInitialData,
    refetch: refetchSessions,
  } = useSessions({
    pullId: effectivePull?.id,
    taskId,
    initialData: sessions,
    sessionsPollingInterval,
  })

  // Repo-owned automation runs publish to their organization's channels, so viewers who don't own the session refresh from the task channel.
  const refreshFromTaskChannel = useCallback(() => {
    const queryClient = getQueryClient()
    void refetchTask()
    void refetchSessions()
    void queryClient.invalidateQueries({queryKey: [SESSION_LOGS_QUERY_KEY]})
    void queryClient.invalidateQueries({queryKey: generateEventsQueryKey(taskId)})
  }, [refetchTask, refetchSessions, taskId])
  useTaskAliveUpdates(currentUserIsSessionOwner ? undefined : taskAliveChannel, refreshFromTaskChannel)

  const normalizedProvisioningPromptText = (provisioningPrompt?.text ?? '').trim()
  const transcriptHasFirstUserTurn =
    provisioningPromptMode === 'until-transcript' &&
    !!provisioningPrompt &&
    (sessionsData?.some(session => (session.event_content ?? '').trim() === normalizedProvisioningPromptText) ?? false)
  const provisioningStartupFailed = provisioningPromptMode === 'until-transcript' && taskStartup?.status === 'failed'
  const displayedProvisioningDraft =
    provisioningPromptMode === 'until-transcript' && (transcriptHasFirstUserTurn || provisioningStartupFailed)
      ? undefined
      : provisioningDraft

  const effectiveLogsPollingInterval = agentProfile.interactiveSession ? 1000 : logsPollingInterval

  const hasActiveSession = sessionsData?.some(isActiveSession) ?? false

  const diffOverlay = useDiffOverlay({
    artifacts: taskData?.artifacts,
    pullBaseRef: effectivePull?.base_ref_name,
    pullHeadRef: effectivePull?.head_ref_name,
    hasActiveSession,
  })

  const diffOverlayWrapperRef = useRef<HTMLDivElement>(null)
  useClampToViewport(diffOverlayWrapperRef, usesDocumentScroll && diffOverlay.isOpen)

  const mostRecentSession = sessionsData?.[sessionsData.length - 1]

  // Fetch mentions for the current sessions
  const {data: mentions, isFetching: isFetchingMentions} = useAgentMentions({
    pull: effectivePull,
    sessions: sessionsData,
    initialData: agentMentions ?? [],
    agentId: mostRecentSession?.agent_id,
  })

  // Manage pending sessions and mentions
  const {allSessions, allMentions, addPendingSession, clearPendingSession} = usePendingSession({
    pullId: effectivePull?.id,
    taskId: effectiveTaskId,
    sessions: sessionsData,
    mentions,
    currentUser,
  })

  const clearPending = () => {
    clearPendingSession()
  }

  const everySessionCCR = sessionsData?.every(s => isCCRAgent(s)) ?? false

  // Sort sessions by creation time
  const sortedSessions = allSessions.sort((a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime())
  const reconciledSessionAiCredits = reconcileSessionAiCredits(sortedSessions)
  const lastSession = allSessions.at(-1)
  const steerableDm = canSteerMessagingAppDm({
    session: lastSession,
    creatorType: taskData?.creator_type,
    viewerOwnsSession: currentUserIsSessionOwner,
  })
  const activeAhpRootSession =
    allSessions.find(s => isActiveSession(s) && s.state !== 'waiting_for_user') ?? allSessions.find(isActiveSession)
  const rootCandidateResumptionKey =
    !taskStartup && activeAhpRootSession?.environment_id
      ? `${activeAhpRootSession.id}\u0000${activeAhpRootSession.environment_id}`
      : ''
  const managedSandboxConversation =
    taskData?.current_environment?.kind === 'managed-sandbox' || isSandboxComputeProvider(taskData?.compute?.provider)
  const awaitIntentAfterLive = managedSandboxConversation && isEnvironmentStatusEnabled()
  const knownNonSandboxConversation = Boolean(taskData) && !managedSandboxConversation
  const continuationMetadataRequired = !taskStartup && Boolean(effectiveTaskId && activeAhpRootSession)
  const continuationMetadataMissing = continuationMetadataRequired && !taskData
  // Slack and Teams DMs stay steerable past the continuation cutoff because the owner can keep talking from Slack or Teams.
  const continuationEligibilityKey =
    managedSandboxConversation && effectiveTaskId && !steerableDm
      ? `${effectiveTaskId}\u0000${taskData?.created_at ?? ''}`
      : ''
  const [continuationCheck, setContinuationCheck] = useState<{key: string; checkedAt: number}>({
    key: '',
    checkedAt: 0,
  })
  useEffect(() => {
    if (!continuationEligibilityKey) return
    const timer = setTimeout(() => {
      setContinuationCheck({key: continuationEligibilityKey, checkedAt: Date.now()})
    }, 0)
    return () => clearTimeout(timer)
  }, [continuationEligibilityKey])
  const rootResumptionSelectionKey = !taskStartup
    ? effectiveTaskId && activeAhpRootSession
      ? `${effectiveTaskId}\u0000${activeAhpRootSession.environment_id ?? ''}\u0000${activeAhpRootSession.id}`
      : rootCandidateResumptionKey
    : ''
  const [rootResumptionRetry, setRootResumptionRetry] = useState({key: '', attempt: 0})
  const rootMetadataRetryScope = useMemo(() => Symbol(rootResumptionSelectionKey), [rootResumptionSelectionKey])
  const [rootMetadataRetryPendingScope, setRootMetadataRetryPendingScope] = useState<symbol>()
  const rootMetadataRetryPending = rootMetadataRetryPendingScope === rootMetadataRetryScope
  const [continuationMetadataRetryPendingScope, setContinuationMetadataRetryPendingScope] = useState<symbol>()
  const continuationMetadataRetryPending = continuationMetadataRetryPendingScope === rootMetadataRetryScope
  const rootResumptionAttemptNumber =
    rootResumptionRetry.key === rootResumptionSelectionKey ? rootResumptionRetry.attempt : 0
  const rootDeferralFlag = useMemo(
    () => ({
      identity: rootResumptionSelectionKey,
      enabled: isFeatureEnabledWithOptOut('one_copilot_chat', 'one_copilot_chat_ui_opt_out'),
    }),
    [rootResumptionSelectionKey],
  ).enabled
  const rootResumptionAttemptKey = rootResumptionSelectionKey
    ? `${rootResumptionSelectionKey}\u0000${rootResumptionAttemptNumber}`
    : ''
  const eagerRootDeadline = currentUserIsSessionOwner && (!rootDeferralFlag || knownNonSandboxConversation)
  const eagerRootResumptionAttempt = useMemo(
    () =>
      instructionsReady && rootResumptionAttemptKey && eagerRootDeadline
        ? createEstablishedAhpResumptionDeadline()
        : undefined,
    [instructionsReady, rootResumptionAttemptKey, eagerRootDeadline],
  )
  const [rootEnvironmentIntentKey, setRootEnvironmentIntentKey] = useState('')
  const pendingRootEnvironmentAttempt = useMemo(
    () =>
      instructionsReady &&
      currentUserIsSessionOwner &&
      rootDeferralFlag &&
      !knownNonSandboxConversation &&
      (rootEnvironmentIntentKey === rootResumptionSelectionKey ||
        (rootCandidateResumptionKey && rootEnvironmentIntentKey === rootCandidateResumptionKey)) &&
      rootResumptionAttemptKey &&
      !eagerRootResumptionAttempt
        ? createEstablishedAhpResumptionDeadline()
        : undefined,
    [
      instructionsReady,
      currentUserIsSessionOwner,
      rootDeferralFlag,
      knownNonSandboxConversation,
      rootEnvironmentIntentKey,
      rootResumptionSelectionKey,
      rootCandidateResumptionKey,
      rootResumptionAttemptKey,
      eagerRootResumptionAttempt,
    ],
  )
  const channelOrigin = steerableDm ? null : getChannelSessionOrigin(lastSession)
  const channelOriginMessage = channelOrigin
    ? `Continue the conversation in the originating ${channelOrigin} channel.`
    : null
  const channelOriginBannerRef = useCallback(
    (element: HTMLDivElement | null) => {
      if (element && channelOriginMessage) {
        announce(channelOriginMessage, {element})
      }
    },
    [channelOriginMessage],
  )
  // Task-level eligibility: if the most recent session is AHP-backed, treat
  // the whole task that way for header/intent rendering. All sessions in a
  // task share an environment so a single check is sufficient.
  const {
    isAhpSession: isAhpTask,
    environmentKind: taskEnvironmentKind,
    environmentId: taskEnvironmentId,
  } = useIsAhpSession(lastSession ?? null, {
    resolveEnvironment: currentUserIsSessionOwner,
    ...(lastSession?.environment_id === activeAhpRootSession?.environment_id && eagerRootResumptionAttempt
      ? {resumptionAttempt: eagerRootResumptionAttempt}
      : {}),
  })
  const ahpSessionOwnsProvisioningPrompt =
    isAhpTask &&
    !!effectiveTaskId &&
    (sessionsData?.some(session => session.task_id === effectiveTaskId && !!session.environment_id) ?? false)
  const displayedProvisioningPrompt =
    transcriptHasFirstUserTurn || provisioningStartupFailed || ahpSessionOwnsProvisioningPrompt
      ? undefined
      : provisioningPrompt

  // For 'readonly' sessions like Copilot code review
  const everySessionReadonly = displayedProvisioningPrompt
    ? false
    : Boolean(sessionsData?.length && sessionsData.every(s => isReadonlyAgent(s)))
  const repolessChatTask = isRepolessChatTask(taskData, repoNwo)
  const sessionEnvironment: SessionEnvironment = agentProfile.interactiveSession
    ? 'cli'
    : taskEnvironmentKind === 'managed-sandbox'
      ? 'sandbox'
      : taskEnvironmentKind
        ? 'environment'
        : 'cloud'
  const interactiveSandboxChat = taskEnvironmentKind === 'managed-sandbox'
  const directTurnTelemetry = useMemo(
    () =>
      directTurnTelemetrySurface &&
      taskEnvironmentKind === 'managed-sandbox' &&
      !establishedAhpBootstrap &&
      !establishedAhpSession
        ? createCopilotChatTurnLatencyTracker()
        : undefined,
    [directTurnTelemetrySurface, establishedAhpBootstrap, establishedAhpSession, taskEnvironmentKind],
  )
  const activeRootEligibility = useIsAhpSession(activeAhpRootSession ?? null, {
    resolveEnvironment: currentUserIsSessionOwner,
    ...(eagerRootResumptionAttempt || pendingRootEnvironmentAttempt
      ? {resumptionAttempt: eagerRootResumptionAttempt ?? pendingRootEnvironmentAttempt}
      : {}),
  })
  const rootStartType = activeRootEligibility.isAhpSession ? startTypeFromEnvironment(activeRootEligibility) : 'unknown'
  // The environment state when the view first connects is the task's start type until a turn
  // cold-starts or wakes it.
  useLayoutEffect(() => {
    recordInitialTaskStartType(scopedTaskId, rootStartType)
  }, [scopedTaskId, rootStartType])
  if (
    rootEnvironmentIntentKey &&
    activeRootEligibility.environmentKind &&
    (activeRootEligibility.environmentKind !== 'managed-sandbox' || !!taskData) &&
    (rootEnvironmentIntentKey === rootResumptionSelectionKey || rootEnvironmentIntentKey === rootCandidateResumptionKey)
  ) {
    setRootEnvironmentIntentKey('')
  }
  const suppliedRootTaskId = activeAhpRootSession?.task_id ?? effectiveTaskId
  const suppliedEstablishedSession =
    !activeAhpRootSession ||
    (suppliedRootTaskId &&
      establishedAhpSession?.identity.taskId === suppliedRootTaskId &&
      establishedAhpSession?.identity.environmentId === activeAhpRootSession.environment_id &&
      establishedAhpSession?.identity.sessionId === activeAhpRootSession.id)
      ? establishedAhpSession
      : undefined
  const suppliedEstablishedSessionLifecycle = useSyncExternalStore(
    suppliedEstablishedSession?.lifecycle.subscribe ?? subscribeToNothing,
    suppliedEstablishedSession?.lifecycle.getState ?? getAbsentEstablishedSessionLifecycle,
    suppliedEstablishedSession?.lifecycle.getState ?? getAbsentEstablishedSessionLifecycle,
  )
  const recoveredFirstTurnPrompt = useMemo(
    () =>
      persistedFirstTurnPrompt(
        activeAhpRootSession?.event_content,
        activeAhpRootSession?.model,
        activeAhpRootSession?.reasoning_effort,
      ),
    [activeAhpRootSession?.event_content, activeAhpRootSession?.model, activeAhpRootSession?.reasoning_effort],
  )
  const activeRootSessionId = activeAhpRootSession?.id
  const firstTurnRecoveryEnabled = useFirstTurnRecoverySelection(
    activeRootSessionId,
    !taskStartup && activeAhpRootSession?.state === SessionState.Queued && recoveredFirstTurnPrompt !== undefined,
  )
  const [rootConnectionSnapshot, setRootConnectionSnapshot] = useState({
    key: rootResumptionSelectionKey,
    live: false,
    failed: false,
    canRetry: true,
  })
  const existingSandboxRoot = !taskStartup && activeRootEligibility.environmentKind === 'managed-sandbox'
  const useEagerRootAttempt = !rootDeferralFlag || !existingSandboxRoot
  useEffect(() => {
    if (!useEagerRootAttempt) eagerRootResumptionAttempt?.dispose()
  }, [eagerRootResumptionAttempt, useEagerRootAttempt])
  const rootHistory = useTaskAhpHistory(activeAhpRootSession?.task_id, undefined, false, {refetchOnMount: false})
  const savedRoot = rootHistory.data?.find(saved => saved.sessionId === activeAhpRootSession?.id)
  const rootSelectionEligibility = continuationMetadataMissing
    ? taskDataIsError
      ? 'invalid'
      : 'pending'
    : !managedSandboxConversation || steerableDm
      ? 'eligible'
      : continuationCheck.key !== continuationEligibilityKey
        ? 'pending'
        : getChatContinuationEligibility(taskData?.created_at, continuationCheck.checkedAt)
  const rootConnectionEligible =
    currentUserIsSessionOwner &&
    instructionsReady &&
    rootSelectionEligibility === 'eligible' &&
    !establishedAhpSessionPending &&
    !suppliedEstablishedSession &&
    !establishedAhpBootstrap &&
    !firstTurnRecoveryEnabled
  const rootConnectionIntent = useDeferredSandboxConnection({
    identity: rootResumptionSelectionKey,
    pendingIdentity:
      rootResumptionSelectionKey !== rootCandidateResumptionKey ? rootCandidateResumptionKey || undefined : undefined,
    flagEnabled: rootDeferralFlag,
    eligible:
      !currentUserIsSessionOwner ||
      rootSelectionEligibility === 'invalid' ||
      rootSelectionEligibility === 'history-only' ||
      suppliedEstablishedSession ||
      establishedAhpBootstrap ||
      firstTurnRecoveryEnabled
        ? false
        : rootSelectionEligibility === 'pending' ||
            !instructionsReady ||
            establishedAhpSessionPending ||
            !activeRootEligibility.environmentKind
          ? undefined
          : rootConnectionEligible && existingSandboxRoot,
    autoConnect:
      currentUserIsSessionOwner &&
      (activeRootEligibility.reachable ||
        activeRootEligibility.environment?.status === 'online' ||
        (rootDeferralFlag &&
          existingSandboxRoot &&
          rootHistory.isFetchedAfterMount &&
          !rootHistory.isFetching &&
          !rootHistory.isError &&
          hasPendingAhpUserPrompt(savedRoot?.bundle))),
    isLive:
      suppliedEstablishedSessionLifecycle === 'live' ||
      (rootConnectionSnapshot.key === rootResumptionSelectionKey && rootConnectionSnapshot.live),
    hasFailed: rootConnectionSnapshot.key === rootResumptionSelectionKey && rootConnectionSnapshot.failed,
    canRetry: rootConnectionSnapshot.key === rootResumptionSelectionKey && rootConnectionSnapshot.canRetry,
  })
  const deferredRootResumptionAttemptKey = rootResumptionAttemptKey
    ? `${rootResumptionAttemptKey}\u0000${rootConnectionIntent.attemptGeneration}`
    : ''
  const shouldCreateRootDeadline =
    continuationMetadataMissing ||
    (!managedSandboxConversation && !existingSandboxRoot && rootSelectionEligibility !== 'pending') ||
    (Boolean(activeRootEligibility.environmentKind) && !existingSandboxRoot) ||
    firstTurnRecoveryEnabled ||
    !!suppliedEstablishedSession ||
    !!establishedAhpBootstrap ||
    rootConnectionIntent.connectEnabled ||
    rootResumptionAttemptNumber > 0
  const deferredRootResumptionAttempt = useMemo(
    () =>
      instructionsReady &&
      deferredRootResumptionAttemptKey &&
      rootDeferralFlag &&
      (!useEagerRootAttempt || !eagerRootResumptionAttempt) &&
      shouldCreateRootDeadline
        ? createEstablishedAhpResumptionDeadline()
        : undefined,
    [
      instructionsReady,
      deferredRootResumptionAttemptKey,
      rootDeferralFlag,
      eagerRootResumptionAttempt,
      useEagerRootAttempt,
      shouldCreateRootDeadline,
    ],
  )
  const rootResumptionAttempt =
    (useEagerRootAttempt ? eagerRootResumptionAttempt : undefined) ??
    pendingRootEnvironmentAttempt ??
    deferredRootResumptionAttempt
  const rootResumptionError = useEstablishedAhpResumptionAttemptError(rootResumptionAttempt)
  const continuationEligibility =
    continuationMetadataMissing && rootResumptionError ? 'invalid' : rootSelectionEligibility
  const historyOnly = continuationEligibility === 'history-only'
  const continuationMetadataError = continuationEligibility === 'invalid'
  const pendingRootAttemptCancellation = useRef<
    {attempt: EstablishedAhpResumptionAttempt; timer: ReturnType<typeof setTimeout>} | undefined
  >(undefined)
  useEffect(() => {
    const pendingCancellation = pendingRootAttemptCancellation.current
    if (rootResumptionAttempt && pendingCancellation?.attempt === rootResumptionAttempt) {
      clearTimeout(pendingCancellation.timer)
      pendingRootAttemptCancellation.current = undefined
    }
    return () => {
      if (!rootResumptionAttempt) return
      const timer = setTimeout(() => {
        rootResumptionAttempt.cancel()
        if (pendingRootAttemptCancellation.current?.timer === timer) {
          pendingRootAttemptCancellation.current = undefined
        }
      }, 0)
      pendingRootAttemptCancellation.current = {attempt: rootResumptionAttempt, timer}
    }
  }, [rootResumptionAttempt])
  const refetchRootEnvironment = activeRootEligibility.refetchEnvironment
  useEffect(() => {
    if (!rootMetadataRetryPending || !refetchRootEnvironment || !rootResumptionAttempt) return
    let current = true
    void (async () => {
      let unexpectedError: Error | undefined
      try {
        await rootResumptionAttempt.waitFor(refetchRootEnvironment())
      } catch (error) {
        if (!rootResumptionAttempt.signal.aborted) {
          unexpectedError = error instanceof Error ? error : new Error(String(error))
        }
      }
      if (current) {
        setRootMetadataRetryPendingScope(pendingScope =>
          pendingScope === rootMetadataRetryScope ? undefined : pendingScope,
        )
      }
      if (unexpectedError) throw unexpectedError
    })()
    return () => {
      current = false
    }
  }, [refetchRootEnvironment, rootMetadataRetryPending, rootMetadataRetryScope, rootResumptionAttempt])
  useEffect(() => {
    if (!continuationMetadataRetryPending || !rootResumptionAttempt) return
    let current = true
    void (async () => {
      let unexpectedError: Error | undefined
      try {
        await rootResumptionAttempt.waitFor(refetchTask({cancelRefetch: false}))
      } catch (error) {
        if (!rootResumptionAttempt.signal.aborted) {
          unexpectedError = error instanceof Error ? error : new Error(String(error))
        }
      }
      if (current) {
        if (continuationEligibilityKey) {
          setContinuationCheck({key: continuationEligibilityKey, checkedAt: Date.now()})
        }
        setContinuationMetadataRetryPendingScope(pendingScope =>
          pendingScope === rootMetadataRetryScope ? undefined : pendingScope,
        )
      }
      if (unexpectedError) throw unexpectedError
    })()
    return () => {
      current = false
    }
  }, [
    continuationEligibilityKey,
    continuationMetadataRetryPending,
    refetchTask,
    rootMetadataRetryScope,
    rootResumptionAttempt,
  ])
  const rootMetadataError =
    activeRootEligibility.isAhpSession && !activeRootEligibility.environment && activeRootEligibility.error
  const activeRootOffline =
    currentUserIsSessionOwner &&
    !taskStartup &&
    !existingSandboxRoot &&
    Boolean(activeAhpRootSession) &&
    activeRootEligibility.isAhpSession &&
    Boolean(activeRootEligibility.environment) &&
    !activeRootEligibility.reachable
  const previousActiveRootOfflineRef = useRef(activeRootOffline)
  const chatInputHadFocusRef = useRef(false)
  const rootIntentKeyRef = useRef<string | undefined>(undefined)
  const offlineNoticeRef = useRef<HTMLDivElement>(null)
  useEffect(() => {
    if (activeRootOffline && !previousActiveRootOfflineRef.current) {
      if (chatInputHadFocusRef.current) {
        offlineNoticeRef.current?.focus()
      } else {
        announce('The environment for this session is offline. Steering will resume when the host reconnects.')
      }
    }
    previousActiveRootOfflineRef.current = activeRootOffline
  }, [activeRootOffline])
  // A refusal that arrives after load removes the chat input. When it had focus,
  // hand focus to the locked state instead of letting it fall back to the page;
  // otherwise announce the lock, since the transcript changed out of view.
  const lockedStateRef = useRef<HTMLDivElement | null>(null)
  const registerLockedState = useCallback((element: HTMLDivElement | null) => {
    if (element) lockedStateRef.current = element
    else if (!lockedStateRef.current?.isConnected) lockedStateRef.current = null
  }, [])
  const previousConversationRefusedRef = useRef(conversationRefused)
  useEffect(() => {
    if (conversationRefused && !previousConversationRefusedRef.current) {
      if (chatInputHadFocusRef.current && lockedStateRef.current) {
        lockedStateRef.current.focus()
        chatInputHadFocusRef.current = false
      } else {
        announce(CONVERSATION_LOCKED_ANNOUNCEMENT, {assertive: true})
      }
    }
    previousConversationRefusedRef.current = conversationRefused
  }, [conversationRefused])
  const rootConnectionEnabled =
    currentUserIsSessionOwner &&
    instructionsReady &&
    (!existingSandboxRoot || !rootConnectionEligible || rootConnectionIntent.connectEnabled) &&
    (!suppliedEstablishedSession || Boolean(establishedAhpBootstrap)) &&
    !establishedAhpSessionPending &&
    !!activeAhpRootSession &&
    continuationEligibility === 'eligible' &&
    // Channel history sessions stay read-only and must not attach alongside the channel client.
    (!isChannelSession(activeAhpRootSession) || steerableDm) &&
    activeRootEligibility.isAhpSession &&
    (existingSandboxRoot || activeRootEligibility.reachable || activeRootEligibility.wakeable)
  const presentationEnvironmentId = activeRootEligibility.environmentId ?? taskEnvironmentId
  const resultPresentation = useCopilotResultPresentationSelection(
    suppliedEstablishedSession?.identity ??
      (activeAhpRootSession?.task_id && presentationEnvironmentId && activeAhpRootSession.id
        ? {
            taskId: activeAhpRootSession.task_id,
            environmentId: presentationEnvironmentId,
            sessionId: activeAhpRootSession.id,
          }
        : undefined),
    directTurnTelemetrySurface ?? 'unknown',
  )
  const observeSelectedSession = useCallback(
    (selected: EstablishedAhpSession) => resultPresentation?.select(selected.identity),
    [resultPresentation],
  )
  const recoveryBaseRef = activeAhpRootSession?.base_ref
  const recoveryEnvironmentKind = activeRootEligibility.environmentKind ?? taskEnvironmentKind
  const recoveryScopeLogin = taskData?.compute?.scope
  const recoveryCreateSession = useMemo(() => {
    return firstTurnRecoveryEnabled
      ? buildAhpRecoveryCreateSession({
          environmentKind: recoveryEnvironmentKind,
          nwo: repoNwo || undefined,
          cloneUrl: taskData?.repository_clone_url,
          baseBranch: recoveryBaseRef,
          scopeLogin: recoveryScopeLogin,
        })
      : undefined
  }, [
    firstTurnRecoveryEnabled,
    recoveryBaseRef,
    recoveryEnvironmentKind,
    recoveryScopeLogin,
    repoNwo,
    taskData?.repository_clone_url,
  ])
  const rootConnection = useMissionControlEstablishedAhpSession({
    awaitIntentAfterLive,
    taskId: activeAhpRootSession?.task_id,
    environmentId: activeRootEligibility.environmentId ?? taskEnvironmentId,
    sessionId: activeAhpRootSession?.id,
    enabled: rootConnectionEnabled,
    wakeable: activeRootEligibility.wakeable,
    recoveryCreateSession,
    instructionScope: repoNwo ? 'repository' : 'chat',
    bootstrap: establishedAhpBootstrap,
    requiresSuppliedBootstrap: requiresEstablishedAhpBootstrap || Boolean(suppliedEstablishedSession),
    resumptionAttempt: rootResumptionAttempt,
    slowWarningEnabled: false,
    directTurnTelemetry,
    onSessionEstablished: resultPresentation ? observeSelectedSession : undefined,
    activeClientRole: steerableDm ? 'never' : 'default',
  })
  const {session: establishedRootSession, error: rootConnectionError} = rootConnection
  if (rootConnectionSnapshot.key !== rootResumptionSelectionKey) {
    setRootConnectionSnapshot({
      key: rootResumptionSelectionKey,
      live: false,
      failed: false,
      canRetry: true,
    })
  } else if (
    rootConnectionSnapshot.live !== rootConnection.live ||
    rootConnectionSnapshot.failed !== Boolean(rootConnection.error || rootConnection.failure) ||
    rootConnectionSnapshot.canRetry !== (rootConnection.canRetry ?? true)
  ) {
    setRootConnectionSnapshot({
      key: rootResumptionSelectionKey,
      live: rootConnection.live,
      failed: Boolean(rootConnection.error || rootConnection.failure),
      canRetry: rootConnection.canRetry ?? true,
    })
  }
  const establishedRootError = currentUserIsSessionOwner
    ? ((continuationMetadataError
        ? (rootResumptionError?.message ??
          'This conversation is missing valid creation metadata. Retry to load it again.')
        : rootConnectionError) ??
      // A live relay keeps the host reachable even if environment metadata fails to load;
      // do not report that metadata failure as a connection error.
      (!activeRootEligibility.environment && !activeRootEligibility.reachable && activeRootEligibility.error
        ? activeRootEligibility.error.message
        : undefined))
    : undefined
  const suppliedConnectionPending =
    !!suppliedEstablishedSession &&
    !establishedAhpBootstrap &&
    (suppliedEstablishedSessionLifecycle === 'connecting' || suppliedEstablishedSessionLifecycle === 'reconnecting') &&
    activeRootEligibility.connectionProgress?.phase !== 'awaiting-intent'
  const suppliedConnectionPresentation = useConnectionAttemptPresentation(
    suppliedConnectionPending,
    suppliedEstablishedSession
      ? `${suppliedEstablishedSession.identity.taskId}:${suppliedEstablishedSession.identity.environmentId}:${suppliedEstablishedSession.identity.sessionId}:${suppliedEstablishedSession.identity.channel ?? ''}`
      : '',
    activeRootEligibility.connectionProgress,
    false,
  )
  const activeEstablishedSession =
    suppliedEstablishedSessionLifecycle === 'live'
      ? suppliedEstablishedSession
      : (establishedRootSession ?? suppliedEstablishedSession)
  const rootAwaitingIntent =
    rootConnection.awaitingIntent ||
    (Boolean(suppliedEstablishedSession) && activeRootEligibility.connectionProgress?.phase === 'awaiting-intent')
  const rootReconnectPendingIntent =
    awaitIntentAfterLive && (rootConnection.reconnecting || suppliedEstablishedSessionLifecycle === 'reconnecting')
  const sessionHasChangeset = useHasSessionChangeset(activeEstablishedSession)
  const hasSessionChangeset =
    isFeatureEnabled('copilot_mission_control_ahp_diff') && (sessionHasChangeset || !!findSavedChangesetUri(savedRoot))
  const openDiffWorkbenchTab = useOpenDiffWorkbenchTab()
  const useDiffWorkbenchTab =
    isInWorkbench &&
    !!openDiffWorkbenchTab &&
    hasSessionChangeset &&
    isFeatureEnabled('copilot_mission_control_ahp_diff_workbench')
  const handleViewDiff = useCallback(
    (filePath?: string) => {
      if (useDiffWorkbenchTab && effectiveTaskId) {
        openDiffWorkbenchTab.open({
          taskId: effectiveTaskId,
          repoNwo,
          headRef: diffOverlay.headRef ?? '',
          initialFilePath: filePath ?? null,
          session: activeEstablishedSession,
          saved: savedRoot,
        })
        return
      }
      diffOverlay.onViewDiff(filePath)
    },
    [
      useDiffWorkbenchTab,
      effectiveTaskId,
      openDiffWorkbenchTab,
      repoNwo,
      diffOverlay,
      activeEstablishedSession,
      savedRoot,
    ],
  )
  useEffect(() => {
    if (!useDiffWorkbenchTab || !effectiveTaskId) return
    openDiffWorkbenchTab.register({
      taskId: effectiveTaskId,
      repoNwo,
      headRef: diffOverlay.headRef ?? '',
      initialFilePath: null,
      session: activeEstablishedSession,
      saved: savedRoot,
    })
  }, [
    useDiffWorkbenchTab,
    effectiveTaskId,
    openDiffWorkbenchTab,
    repoNwo,
    diffOverlay.headRef,
    activeEstablishedSession,
    savedRoot,
  ])
  const directTurnTelemetryContext = resolveDirectTurnTelemetryContext({
    surface: directTurnTelemetrySurface,
    environmentKind: taskEnvironmentKind,
    taskId: effectiveTaskId,
    taskData,
    session: activeEstablishedSession,
  })
  const firstTurnRecovery = useAhpFirstTurnRecovery({
    session: establishedRootSession,
    prompt: recoveredFirstTurnPrompt,
    sessionId: activeRootSessionId,
    taskId: effectiveTaskId,
    interactiveSandboxChat: interactiveSandboxChat && !steerableDm,
    directTurnTelemetryContext,
    enabled: !!recoveryCreateSession,
  })
  const rootResumeConnectionError = currentUserIsSessionOwner
    ? (establishedRootError ??
      (suppliedEstablishedSessionLifecycle === 'failed' && !establishedAhpBootstrap
        ? 'The session connection was lost.'
        : undefined))
    : undefined
  const rootResumeDraftOnly = suppliedEstablishedSessionLifecycle !== 'live' && !rootConnection.live
  const rootResumeConnectionPending =
    !rootResumeConnectionError &&
    !rootConnection.failure &&
    (rootConnection.showProgress || suppliedConnectionPresentation.showProgress)
  const retryRootMetadata = () => {
    if (!rootResumptionSelectionKey || rootMetadataRetryPending) return
    if (continuationEligibilityKey) {
      setContinuationCheck({key: continuationEligibilityKey, checkedAt: Date.now()})
    }
    setRootMetadataRetryPendingScope(rootMetadataRetryScope)
    setRootResumptionRetry(current => ({
      key: rootResumptionSelectionKey,
      attempt: current.key === rootResumptionSelectionKey ? current.attempt + 1 : 1,
    }))
  }
  const retryRootConnection = () => {
    if (!instructionsReady) return
    const checkedAt = Date.now()
    if (continuationEligibilityKey) {
      setContinuationCheck({key: continuationEligibilityKey, checkedAt})
      if (getChatContinuationEligibility(taskData?.created_at, checkedAt) !== 'eligible') return
    }
    if (rootResumptionSelectionKey) {
      setRootResumptionRetry(current => ({
        key: rootResumptionSelectionKey,
        attempt: current.key === rootResumptionSelectionKey ? current.attempt + 1 : 1,
      }))
    }
    rootConnection.connect()
  }
  const requestRootConnection = () => {
    if (!rootResumptionSelectionKey) return
    if (rootAwaitingIntent || rootReconnectPendingIntent) {
      rootIntentKeyRef.current = rootResumptionSelectionKey
      void activeEstablishedSession?.commands.ensureLive?.()
      return
    }
    if (!rootDeferralFlag) return
    rootIntentKeyRef.current = rootResumptionSelectionKey
    if (rootConnectionIntent.phase === 'live') {
      void establishedRootSession?.commands.ensureLive?.()
      return
    }
    if (rootConnectionIntent.phase !== 'deferred' && !rootConnectionIntent.canRequestRetry) return
    if (continuationEligibilityKey) {
      const checkedAt = Date.now()
      setContinuationCheck({key: continuationEligibilityKey, checkedAt})
      if (getChatContinuationEligibility(taskData?.created_at, checkedAt) !== 'eligible') return
    }
    const retry = rootConnectionIntent.canRequestRetry
    if (!knownNonSandboxConversation && !activeRootEligibility.environmentKind) {
      setRootEnvironmentIntentKey(rootResumptionSelectionKey)
    }
    rootConnectionIntent.requestConnection()
    if (retry) rootConnection.connect()
  }
  const retryContinuationMetadata = () => {
    if (!rootResumptionSelectionKey || continuationMetadataRetryPending) return
    setContinuationMetadataRetryPendingScope(rootMetadataRetryScope)
    setRootResumptionRetry(current => ({
      key: rootResumptionSelectionKey,
      attempt: current.key === rootResumptionSelectionKey ? current.attempt + 1 : 1,
    }))
  }
  const newConversationHref = (() => {
    const params = new URLSearchParams({new_session: '1'})
    if (repoNwo) params.set('repo', repoNwo)
    return `${getAgentsPath()}?${params.toString()}`
  })()
  const startNewConversation = () => navigate(newConversationHref)
  const onRootResume = currentUserIsSessionOwner
    ? continuationMetadataError
      ? retryContinuationMetadata
      : rootMetadataError
        ? retryRootMetadata
        : !suppliedEstablishedSession || establishedAhpBootstrap
          ? retryRootConnection
          : undefined
    : undefined
  const autoRetryRootConnection =
    onRootResume === retryRootConnection && Boolean(rootConnectionError) && rootConnection.canRetry !== false
  const hasRootIntent = () => {
    const recorded = rootIntentKeyRef.current
    if (!recorded || !rootResumptionSelectionKey) return false
    return recorded === rootResumptionSelectionKey || recorded === rootCandidateResumptionKey
  }
  // Visibility only retries after the user asked to resume, so an idle tab never wakes compute.
  const retryRootConnectionOnRecovery = useEffectEvent((event: Event) => {
    if (document.visibilityState !== 'visible') return
    if (event.type === 'visibilitychange' && !hasRootIntent()) return
    retryRootConnection()
  })
  useEffect(() => {
    if (!autoRetryRootConnection) return
    window.addEventListener('online', retryRootConnectionOnRecovery)
    document.addEventListener('visibilitychange', retryRootConnectionOnRecovery)
    return () => {
      window.removeEventListener('online', retryRootConnectionOnRecovery)
      document.removeEventListener('visibilitychange', retryRootConnectionOnRecovery)
    }
  }, [autoRetryRootConnection])
  useAwaitingIntentWake({
    identityKey: rootResumptionSelectionKey,
    awaitingIntent: currentUserIsSessionOwner && rootAwaitingIntent,
    session: activeEstablishedSession,
    environmentStatus: activeRootEligibility.environment?.status,
    refetchEnvironment: async () => {
      await refetchRootEnvironment?.()
      const queryClient = getQueryClient()
      const queryKey = agentEnvironmentQueryKey(activeRootEligibility.environmentId)
      if (refetchRootEnvironment && queryClient.getQueryState(queryKey)?.status !== 'success') return undefined
      const environment = queryClient.getQueryData<Environment | null>(queryKey)
      const currentEnvironment = refetchRootEnvironment
        ? environment
        : (environment ?? activeRootEligibility.environment)
      return currentEnvironment?.status
    },
  })
  useEffect(() => {
    if (activeEstablishedSession) onEstablishedAhpSession?.(activeEstablishedSession)
  }, [activeEstablishedSession, onEstablishedAhpSession])
  const replacementBootstrapPending =
    requiresEstablishedAhpBootstrap &&
    !!establishedAhpSession &&
    !suppliedEstablishedSession &&
    !establishedAhpBootstrap
  const replacementBootstrapError = replacementBootstrapPending ? requiredBootstrapError : undefined
  const handleUserRename = useCallback(
    (title: string) => {
      if (onUserRename) activeEstablishedSession?.commands.renameSession(title)
      onUserRename?.(title)
    },
    [activeEstablishedSession, onUserRename],
  )
  const markerSessionKey = activeEstablishedSession
    ? `${effectiveTaskId}:${activeEstablishedSession.identity.sessionId}`
    : undefined

  // Notices describe picker changes made during the current visit to a session,
  // so discard markers left over from a different session.
  if (markerSessionKey && modelChangeState.sessionKey && modelChangeState.sessionKey !== markerSessionKey) {
    setModelChangeState({markers: []})
  }

  const appendModelChangeMarker = useCallback(
    (marker: Omit<ModelChangeMarker, 'id' | 'afterTurnId'>) => {
      if (!activeEstablishedSession || !markerSessionKey) return
      const state = activeEstablishedSession.view.getSnapshot().state
      const afterTurnId = state ? (getTurns(state).at(-1)?.id ?? null) : null
      markerSequence.current += 1
      setModelChangeState(current => ({
        sessionKey: markerSessionKey,
        markers: [
          ...(current.sessionKey === markerSessionKey ? current.markers : []),
          {...marker, id: `model-change-${markerSequence.current}`, afterTurnId},
        ],
      }))
    },
    [activeEstablishedSession, markerSessionKey],
  )
  const handleModelChange = useCallback(
    (modelId: string, reasoningEffort: ReasoningEffort | undefined) =>
      appendModelChangeMarker({kind: 'model', modelId, reasoningEffort}),
    [appendModelChangeMarker],
  )
  const handleReasoningEffortChange = useCallback(
    (fromReasoningEffort: ReasoningEffort | undefined, toReasoningEffort: ReasoningEffort | undefined) =>
      appendModelChangeMarker({kind: 'reasoning', fromReasoningEffort, toReasoningEffort}),
    [appendModelChangeMarker],
  )
  const timelineMarkers = useMemo<TimelineMarker[]>(
    () =>
      (modelChangeState.sessionKey === markerSessionKey ? modelChangeState.markers : []).map(marker => ({
        id: marker.id,
        afterTurnId: marker.afterTurnId,
        node: <ModelChangeStatusEntry marker={marker} />,
      })),
    [markerSessionKey, modelChangeState],
  )
  const canEstablishInternally = !requiresEstablishedAhpBootstrap || !!establishedAhpBootstrap
  const establishedSessionPending =
    establishedAhpSessionPending ||
    (canEstablishInternally &&
      !!activeAhpRootSession &&
      activeRootEligibility.isAhpSession &&
      (activeRootEligibility.reachable || activeRootEligibility.wakeable) &&
      !activeEstablishedSession)
  const quickChatConnectionPending =
    !existingSandboxRoot &&
    ((announceEstablishedAhpSessionPending && establishedSessionPending) || replacementBootstrapPending) &&
    !activeEstablishedSession
  const quickChatConnectionError = replacementBootstrapError
    ? replacementBootstrapError
    : establishedRootError
      ? 'The active AHP session could not be connected.'
      : undefined
  const provisioningConnectionError =
    provisioningPromptMode === 'until-transcript' ? quickChatConnectionError : undefined
  const effectiveSessionLoadingMessage =
    sessionLoadingMessage && !activeEstablishedSession && !quickChatConnectionError && !provisioningStartupFailed
      ? sessionLoadingMessage
      : undefined
  const displayedSessions = sortedSessions
  const connectionAnnouncementRef = useRef<string | undefined>(undefined)
  useEffect(() => {
    const errorMessage = onEnvironmentTaskLaunchAction ? undefined : requiredBootstrapError
    if (!errorMessage) {
      connectionAnnouncementRef.current = undefined
      return
    }
    if (connectionAnnouncementRef.current === errorMessage) return
    connectionAnnouncementRef.current = errorMessage
    announce(errorMessage, {assertive: true})
  }, [onEnvironmentTaskLaunchAction, requiredBootstrapError])
  useReportUserVisibleError(
    requiredBootstrapError
      ? onEnvironmentTaskLaunchAction
        ? 'environment_unavailable'
        : 'session_connect_failed'
      : undefined,
    {taskId: scopedTaskId, identity: requiredBootstrapError},
  )
  // Managed CCA (the GitHub-managed Copilot cloud agent environment) still
  // originates from an issue/PR assignment, so unlike other AHP kinds it DOES
  // have meaningful "started a task" context and should show the initial message.
  const isManagedCcaTask = isAhpTask && taskEnvironmentKind === 'managed-cca'
  const legacyAhpSession =
    isAhpTask && !isManagedCcaTask && displayedSessions[0]?.event_type === 'legacy_chat'
      ? displayedSessions[0]
      : undefined
  const coordinateLegacyPrompt = !!legacyAhpSession
  const legacyPromptText = `${legacyAhpSession?.event_content ?? ''}`.trim()
  const legacyPromptKey = `${effectiveTaskId ?? ''}\0${legacyAhpSession?.id ?? ''}\0${legacyPromptText}`
  const [legacyPromptOwnership, setLegacyPromptOwnership] = useState<{key: string; turnIds: Set<string>}>(() => ({
    key: legacyPromptKey,
    turnIds: new Set(),
  }))
  if (legacyPromptOwnership.key !== legacyPromptKey) {
    setLegacyPromptOwnership({key: legacyPromptKey, turnIds: new Set()})
  }
  const reportLegacyPromptVisibility = useCallback(
    (turnId: string, visible: boolean) => {
      setLegacyPromptOwnership(current => {
        if (current.key !== legacyPromptKey || current.turnIds.has(turnId) === visible) return current
        const turnIds = new Set(current.turnIds)
        if (visible) turnIds.add(turnId)
        else turnIds.delete(turnId)
        return {key: legacyPromptKey, turnIds}
      })
    },
    [legacyPromptKey],
  )
  const legacyInitialMessage = useMemo(
    () =>
      coordinateLegacyPrompt
        ? {
            text: legacyPromptText,
            transcriptAttachmentLabel: 'Previous conversation',
            report: reportLegacyPromptVisibility,
          }
        : undefined,
    [coordinateLegacyPrompt, legacyPromptText, reportLegacyPromptVisibility],
  )
  const ahpOwnsLegacyPrompt =
    coordinateLegacyPrompt && legacyPromptOwnership.key === legacyPromptKey && legacyPromptOwnership.turnIds.size > 0
  const standaloneProvisioningPrompt = ahpOwnsLegacyPrompt ? undefined : displayedProvisioningPrompt
  const provisioningOwnsLegacyPrompt =
    !!standaloneProvisioningPrompt && standaloneProvisioningPrompt.text.trim() === legacyPromptText

  const {planStates, hasPendingPlan, pendingPlanContent, setPendingPlanContent, updatePlanState} = usePendingPlanGate({
    sessionsData,
    lastSessionId: lastSession?.id,
  })

  const {
    data: inactiveLogs,
    isLoading: inactiveLogsIsLoading,
    isFetching: inactiveLogsIsFetching,
  } = useInactiveLogs({
    sessions: sortedSessions,
    pullId: effectivePull?.id,
    repoOwner,
    repoName,
    taskId,
  })

  // Extract thread ID from the copilot-chat-spec-task session's event identifiers (feature flagged)
  const isTaskHyperlinkingEnabled = isFeatureEnabled('copilot_immersive_task_hyperlinking')
  const threadId = isTaskHyperlinkingEnabled
    ? (() => {
        const specTaskSession = allSessions.find(session => session.event_type === COPILOT_CHAT_SPEC_TASK_EVENT_TYPE)
        return extractSpecTaskThreadIdFromEventIdentifiers(specTaskSession?.event_identifiers)
      })()
    : undefined

  const taskAuthor = useTaskAuthor(taskData, repoOwner, repoName)

  // Function to get the mention for a specific session
  const getMentionForSession = (session: SessionType): AgentMention | undefined => {
    if (!allMentions || !session.event_identifiers) {
      return undefined
    }

    // There should only be one mention
    return allMentions.find(mention => session.event_identifiers?.includes(mention.id.toString()))
  }

  // Determine which session should be expanded by default
  const getDefaultOpenState = (sessionId: string, isPending: boolean) => {
    // CLI sessions should always be expanded
    if (agentProfile.interactiveSession) {
      return true
    }

    if (urlSessionId) {
      // If a specific session ID is provided in URL, expand that session
      return sessionId === urlSessionId
    }

    // We're expanding pending sessions by default
    if (isPending) {
      return true
    }

    // If there's an active session, that one takes precedence unless explicitly provided above
    if (sessionsData && sessionsData.some(session => isActiveSession(session))) {
      return sessionId === sessionsData.find(session => isActiveSession(session))?.id
    }
    return false
  }

  const {isAtBottom, scrollToBottom, containerCallbackRef, contentCallbackRef} = useAutoScroll({
    containerRef: scrollContainerRef,
    scrollTarget: usesDocumentScroll ? 'document' : 'container',
    resetKey: effectiveTaskId,
    bottomThreshold: 4,
  })
  const mergedScrollContainerRef = useMergedRefs(scrollContainerRef, containerCallbackRef)

  // Adapter for TaskChatInputV2 which passes an offset option (no longer needed since
  // useAutoScroll now scrolls the container directly to the bottom)
  const scrollToBottomCompat = useCallback(
    (_options?: {offset?: number}) => {
      scrollToBottom()
    },
    [scrollToBottom],
  )

  const handleCommandSent = useCallback(() => {
    // defer to next frame so that the new message is rendered before we attempt to scroll
    requestAnimationFrame(() => {
      scrollToBottom()
    })
  }, [scrollToBottom])

  const handleScrollToBottomClick = useCallback(() => {
    requestAnimationFrame(() => {
      scrollContainerRef.current?.focus({preventScroll: true})
      scrollToBottom('smooth')
    })
  }, [scrollToBottom])

  const removeHighlight = useCallback(() => {
    if (!urlSessionId) return
    if (typeof window === 'undefined') return
    if (sessionIdClearedRef.current) return

    sessionIdClearedRef.current = true
    const newParams = new URLSearchParams(searchParams)
    newParams.delete('session_id')
    const searchString = newParams.toString()
    const newUrl = `${window.location.pathname}${searchString ? `?${searchString}` : ''}${window.location.hash}`
    addUrlToHistoryStack(newUrl)
    queueMicrotask(() => setHighlightedSessionId(null))
  }, [searchParams, urlSessionId])

  // Remove session_id from URL when a new session becomes active.
  useEffect(() => {
    if (!urlSessionId || !sessionsData) return

    const activeSession = sessionsData.find(isActiveSession)
    const activeSessionId = activeSession?.id ?? null

    if (!activeSessionId || activeSessionId === urlSessionId) return

    removeHighlight()
  }, [urlSessionId, sessionsData, removeHighlight])

  // If session_id changes in the URL, reset sessionIdClearedRef to allow possible removal of it from the URL.
  useEffect(() => {
    sessionIdClearedRef.current = false
  }, [urlSessionId])

  // Handle diff events to populate chat input with file references
  useDiffEventHandler({
    chatInputRef,
    textAreaRef,
    isReadonly: everySessionReadonly,
    taskId: effectiveTaskId ?? '',
  })

  // Clear chat input when switching tasks/pulls
  useEffect(() => {
    if (chatInputRef.current) {
      chatInputRef.current.setText('')
    }
  }, [effectiveTaskId, urlSessionId])

  const sessionsTerminalError = sessionsIsError && sessionsTimedOut
  const sessionsInitialLoading =
    !sessionsTerminalError && (!sessionsData || (!sessionsData.length && !notUsingSessionInitialData))
  const fallbackState =
    displayedProvisioningPrompt ||
    effectiveSessionLoadingMessage ||
    !instructionsReady ||
    sessionsInitialLoading ? null : sessionsTerminalError ? (
      <CenteredBlankSlate
        title="Activity cannot be loaded"
        description="The activity on this task is temporarily unavailable due to a system error. Try reloading the page, or if the problem persists, contact support."
        statusLink="https://www.githubstatus.com/"
      />
    ) : !sessionsData?.length ? (
      <CenteredBlankSlate
        title="This task is not viewable"
        description={`You can't view this task because you do not have any active sessions${
          effectivePull ? ' for this pull request' : ''
        }.`}
        pullLink={effectivePull?.url}
      />
    ) : null
  const stickyHeaderEnabled = isFeatureEnabled('copilot_mission_control_sticky_header')
  const showVerbosityControl = shouldShowTimelineVerbosityControl({
    showHeader,
    stickyHeaderEnabled,
    isAhpSession: isAhpTask,
  })
  if (fallbackState) {
    return showHeader && (useSidebarPageLayoutHeader || stickyHeaderEnabled) ? (
      <div className={styles.fallbackStateContainer}>
        <TaskSidebarFallbackToggle />
        {fallbackState}
      </div>
    ) : (
      fallbackState
    )
  }

  const requiredBootstrapUnavailable =
    requiresEstablishedAhpBootstrap && !establishedAhpBootstrap && !activeEstablishedSession
  const composerTask = taskData ?? task
  const taskChatInputProps =
    composerTask && !hideChatInput && !conversationRefused
      ? {
          interactiveSandboxChat,
          taskAgentId: allSessions[0]?.agent_id,
          activeSession:
            // Prefer an active session that's actively doing work over
            // a paused (waiting_for_user) one. During a resume, the OLD
            // session is permanently waiting_for_user (frozen) while the
            // NEW resume session is in_progress; we want UI affordances
            // (Stop button, chat input header, etc.) to track the live
            // resume session, not the frozen original.
            allSessions?.find(s => isActiveSession(s) && s.state !== 'waiting_for_user') ??
            allSessions?.find(isActiveSession) ??
            null,
          onAddPendingSession: addPendingSession,
          onClearPendingSession: clearPending,
          payload: effectivePayload,
          scrollToBottom: scrollToBottomCompat,
          repoNwo,
          task: composerTask,
          branchAliveChannel,
          textAreaRef,
          onCloseDiff: diffOverlay.onCloseDiff,
          onViewDiff: handleViewDiff,
        }
      : null
  const canCreateManagedCcaFollowUp =
    (taskEnvironmentKind ?? taskData?.current_environment?.kind) === 'managed-cca' &&
    !!lastSession &&
    TERMINAL_SESSION_STATES.has(lastSession.state) &&
    !activeAhpRootSession &&
    !activeEstablishedSession &&
    !requiresEstablishedAhpBootstrap &&
    !taskStartup &&
    !provisioningPrompt
  const shouldRenderEstablishedTaskChatInput =
    !!taskChatInputProps &&
    !canCreateManagedCcaFollowUp &&
    (isAhpTask ||
      activeEstablishedSession ||
      requiresEstablishedAhpBootstrap ||
      (isFeatureEnabled('copilot_mission_control_environments_web_kickoff') && !!taskData?.current_environment) ||
      managedSandboxConversation ||
      provisioningPrompt ||
      taskStartup)
  const composerDraftOnly =
    !instructionsReady ||
    !activeEstablishedSession ||
    requiredBootstrapUnavailable ||
    continuationEligibility !== 'eligible' ||
    rootResumeDraftOnly ||
    firstTurnRecovery.pending ||
    activeRootOffline ||
    Boolean(pendingTaskStartupPrompt || displayedProvisioningPrompt || effectiveSessionLoadingMessage)
  const rootResumeConnectionErrorIsIndependent =
    Boolean(rootResumeConnectionError) && !requiredBootstrapError && !taskStartup
  const preparationPhase = taskStartup ? taskStartup.phase : provisioning?.phase
  const pageLoadingSources = getPageLoadingSources({
    lookup: {
      pending: Boolean(
        sessionsInitialLoading ||
        !instructionsReady ||
        effectiveSessionLoadingMessage ||
        continuationEligibility === 'pending',
      ),
      error: Boolean(continuationMetadataError || sessionsTerminalError),
    },
    preparation: {
      pending: Boolean(displayedProvisioningPrompt && !provisioningConnectionError),
      error: provisioningStartupFailed,
      phase: preparationPhase === 'preparing' || preparationPhase === 'cloning' ? preparationPhase : undefined,
      progress: cloneProgress,
      launchKind:
        preparationPhase === 'connecting'
          ? taskStartup?.launch?.kind
          : !taskStartup && !effectiveTaskId && provisioningPrompt
            ? provisionalLaunchKind
            : undefined,
    },
    connection: {
      pending: Boolean(
        quickChatConnectionPending ||
        (requiredBootstrapUnavailable && suppliedEstablishedSession) ||
        rootResumeConnectionPending,
      ),
      error: Boolean(quickChatConnectionError || requiredBootstrapError || rootResumeConnectionError),
      replacesLoading: !rootResumeConnectionErrorIsIndependent,
      phase:
        rootConnection.presentationPhase ??
        suppliedConnectionPresentation.presentationPhase ??
        (quickChatConnectionPending || (requiredBootstrapUnavailable && suppliedEstablishedSession)
          ? 'connecting'
          : undefined),
    },
  })
  const taskChat = (
    <TaskActionProviders
      navigateOnDelete={navigateOnDelete}
      deletionDestination={deletionDestination}
      onDeleteSuccess={onDeleteSuccess}
      chatApiUrl={chatApiUrl}
      onTaskStateChange={onTaskStateChange}
    >
      <TaskChatSessionProvider
        session={activeEstablishedSession}
        saved={savedRoot}
        resultPresentation={resultPresentation}
        recoveryControls={Boolean(shouldRenderEstablishedTaskChatInput)}
      >
        <AgentProfileProvider slug={profileSlugOverride ?? taskData?.agent_collaborators?.[0]?.slug}>
          <OptimisticLogsProvider
            sessionId={agentProfile.interactiveSession ? lastSession?.id : undefined}
            taskId={isAhpTask ? undefined : effectiveTaskId}
          >
            <div className={clsx(styles.root, usesDocumentScroll && styles.documentScrollRoot)}>
              <div className={styles.container}>
                <div ref={diffOverlayWrapperRef} className={styles.diffOverlayWrapper}>
                  <div
                    className={clsx(styles.stickableContainer, diffOverlay.isOpen && styles.stickableContainerFrozen)}
                  >
                    {showHeader && stickyHeaderEnabled && (
                      <AgentsWorkbenchHeader hidden={diffOverlay.isOpen}>
                        <StickyTaskChatHeader
                          showVerbosityControl={showVerbosityControl}
                          sessions={sortedSessions}
                          pull={effectivePull}
                          task={taskData}
                          repoOwner={repoOwner}
                          repoName={repoName}
                          hideArchive={hideArchive}
                          hideActions={hideActions}
                          hideEnvironmentVisual={hideEnvironmentVisual}
                          isCodeReviewMode={everySessionCCR}
                          dataUpdatedAt={taskDataUpdatedAt}
                          onUserRename={handleUserRename}
                          chatApiUrl={chatApiUrl}
                        />
                      </AgentsWorkbenchHeader>
                    )}
                    <div
                      ref={mergedScrollContainerRef}
                      className={styles.scrollContainer}
                      role="region"
                      // Keyboard users need to focus the overflow region to scroll task activity.
                      tabIndex={0}
                      aria-label="Task activity"
                    >
                      <ScrollToBottomButton isAtBottom={isAtBottom} onScrollToBottom={handleScrollToBottomClick} />
                      <div
                        ref={contentCallbackRef}
                        className={clsx(
                          styles.content,
                          isSidePanel && styles.sidePanelContent,
                          showHeader && stickyHeaderEnabled && styles.stickyHeaderContent,
                        )}
                      >
                        <ArchiveErrorBanner />
                        {requiredBootstrapError &&
                          (onEnvironmentTaskLaunchAction ? (
                            <EnvironmentTaskLaunchFailureBanner
                              error={requiredBootstrapError}
                              onAction={onEnvironmentTaskLaunchAction}
                              actionLabel={environmentTaskLaunchActionLabel}
                              actionFocusRef={scrollContainerRef}
                            />
                          ) : (
                            <Banner
                              variant="critical"
                              title="Unable to connect to the session"
                              description={requiredBootstrapError}
                            />
                          ))}
                        {showHeader && !stickyHeaderEnabled && (
                          <AgentsWorkbenchHeader padded hidden={diffOverlay.isOpen}>
                            <TaskChatHeader
                              sessions={sortedSessions}
                              currentSessionId={urlSessionId ?? undefined}
                              pull={effectivePull}
                              task={taskData}
                              repoOwner={repoOwner}
                              repoName={repoName}
                              hideArchive={hideArchive}
                              hideActions={hideActions}
                              hideEnvironmentVisual={hideEnvironmentVisual}
                              isCodeReviewMode={everySessionCCR}
                              dataUpdatedAt={taskDataUpdatedAt}
                              onUserRename={handleUserRename}
                              chatApiUrl={chatApiUrl}
                              useSidebarPageLayoutHeader={useSidebarPageLayoutHeader}
                            />
                          </AgentsWorkbenchHeader>
                        )}
                        {standaloneProvisioningPrompt && (
                          <QuickChatProvisioningUserMessage prompt={standaloneProvisioningPrompt} />
                        )}
                        {displayedSessions.map((session, index) => {
                          const isFirstSession = index === 0
                          const mention = getMentionForSession(session)
                          const isPending = isPendingSession(session)
                          const isReadonly = isReadonlyAgent(session)
                          const isCCR = isCCRAgent(session)
                          const isLegacyChatSession = isFirstSession && session.event_type === 'legacy_chat'
                          const shouldRenderInitialMessage =
                            isLegacyChatSession ||
                            (isFirstSession &&
                              !agentProfile.interactiveSession &&
                              !agentProfile.agentProvidesIntent &&
                              (!isAhpTask || isManagedCcaTask) &&
                              (!taskAuthor.isAutomation || !taskAuthor.isLoading || !!session.event_content))
                          const showSessionMetadata =
                            isLegacyChatSession ||
                            (shouldRenderInitialMessage && isFeatureEnabled('copilot_mission_control_session_metadata'))
                          const initialMessage = isCCR
                            ? 'requested a review'
                            : showSessionMetadata
                              ? session.event_type === 'parent_session'
                                ? 'created this session'
                                : undefined
                              : 'started a task'

                          return (
                            <div className="d-flex flex-column" key={session.id}>
                              {/* Skip initial messages for interactive sessions and AHP-backed
                                sessions — neither has meaningful "started a task" context —
                                except Managed CCA, which is still an issue/PR assignment. */}
                              {shouldRenderInitialMessage && (
                                <div
                                  className={clsx(
                                    styles.initialMessageContainer,
                                    !showSessionMetadata && styles.initialMessages,
                                  )}
                                >
                                  <UserInitialMessage
                                    currentUser={currentUser}
                                    threadId={threadId}
                                    session={session}
                                    message={initialMessage}
                                    environment={sessionEnvironment}
                                    agentId={session.agent_id}
                                    showSessionMetadata={showSessionMetadata}
                                    {...(isLegacyChatSession && coordinateLegacyPrompt
                                      ? {suppressPrompt: ahpOwnsLegacyPrompt || provisioningOwnsLegacyPrompt}
                                      : {})}
                                    isTaskCreatedByAutomation={taskAuthor.isAutomation}
                                    automationDisplayName={taskAuthor.isAutomation ? taskAuthor.displayName : undefined}
                                    automationPrompt={taskAuthor.isAutomation ? taskAuthor.prompt : undefined}
                                  />
                                </div>
                              )}
                              {/* Subsequent sessions: show PR comment mention or problem statement */}
                              {!isFirstSession && !isReadonly && (mention || isFetchingMentions) && (
                                <UserMessage
                                  key={mention?.id}
                                  mention={mention}
                                  isLoading={!mention && isFetchingMentions}
                                  agentId={session.agent_id}
                                />
                              )}
                              {!isFirstSession && !isReadonly && !mention && !isFetchingMentions && (
                                <UserProblemStatementMessage session={session} />
                              )}
                              <SessionContextProvider
                                session={session}
                                reconciledAiCredits={reconciledSessionAiCredits.get(session.id)}
                                logs={inactiveLogs?.[session.id]}
                                logsAreLoading={
                                  inactiveLogsIsLoading ||
                                  (inactiveLogsIsFetching && inactiveLogs?.[session.id] === undefined)
                                }
                                taskSharingStatus={taskData?.sharing_status}
                              >
                                <LegacyChatInitialMessageContext
                                  value={isLegacyChatSession ? legacyInitialMessage : undefined}
                                >
                                  <PageLoadingSession
                                    defaultOpen={getDefaultOpenState(session.id, isPending)}
                                    onOpenChange={open => {
                                      if (!open && session.id === highlightedSessionId) {
                                        removeHighlight()
                                      }
                                    }}
                                    shouldHighlight={session.id === highlightedSessionId}
                                    pull={effectivePull}
                                    logsPollingInterval={effectiveLogsPollingInterval}
                                    nwo={repoNwo}
                                    taskAliveChannel={taskAliveChannel}
                                    taskCreatorType={taskData?.creator_type}
                                    isRepolessChatTask={repolessChatTask}
                                    establishedAhpSession={
                                      session.id === activeAhpRootSession?.id ? activeEstablishedSession : undefined
                                    }
                                    establishedAhpSessionRequired={
                                      !instructionsReady ||
                                      (session.id === activeAhpRootSession?.id && establishedSessionPending)
                                    }
                                    timelineMarkers={
                                      session.id === activeAhpRootSession?.id ? timelineMarkers : undefined
                                    }
                                    queueReadOnly={
                                      session.id === activeAhpRootSession?.id ? rootResumeDraftOnly : undefined
                                    }
                                    preserveAhpHistory={
                                      session.id === activeAhpRootSession?.id && managedSandboxConversation
                                    }
                                    onContributedInstructions={openAppliedInstructions}
                                    loadingPresentation="page"
                                    artifacts={index === displayedSessions.length - 1 ? taskData?.artifacts : undefined}
                                  />
                                </LegacyChatInitialMessageContext>
                                {session.state === 'completed' && (
                                  <>
                                    {/* Copilot never has a reply on the first session, but in single session view we can't tell, so show it to be safe */}
                                    {((currentUserIsSessionOwner && !isFirstSession) || !currentUserIsSessionOwner) && (
                                      <CopilotReplyMessage logsPollingInterval={effectiveLogsPollingInterval ?? 3000} />
                                    )}
                                    {session.event_type === 'pull_request_comment' ? (
                                      <SessionMetadataBar placement="outside-session" />
                                    ) : (
                                      <CopilotFinalStopMessage
                                        logsPollingInterval={effectiveLogsPollingInterval ?? 3000}
                                        onApprove={
                                          planStates[session.id] === PLAN_STATE_APPROVED
                                            ? undefined
                                            : handlePlanApprove(session)
                                        }
                                        planState={planStates[session.id]}
                                        onPlanDetected={
                                          index === sortedSessions.length - 1 && !planStates[session.id]
                                            ? setPendingPlanContent
                                            : undefined
                                        }
                                      />
                                    )}
                                  </>
                                )}
                              </SessionContextProvider>
                            </div>
                          )
                        })}
                        <SessionLoadingIndicator />
                      </div>
                    </div>
                  </div>
                  <div
                    aria-hidden
                    className={clsx(styles.timelineOverlayWrapper, !isAtBottom && styles.timelineOverlayWrapperVisible)}
                  />
                  {effectiveTaskId &&
                    (hasSessionChangeset ||
                      (!effectivePull && diffOverlay.baseRef && diffOverlay.headRef && repoNwo)) && (
                      <DiffOverlay
                        isOpen={diffOverlay.isOpen}
                        onClose={diffOverlay.close}
                        repoNwo={repoNwo}
                        taskId={effectiveTaskId}
                        baseRef={diffOverlay.baseRef ?? ''}
                        headRef={diffOverlay.headRef ?? ''}
                        initialFilePath={diffOverlay.initialFilePath}
                      />
                    )}
                </div>
                {!hideChatInput &&
                  !conversationRefused &&
                  (channelOrigin ? (
                    <div ref={channelOriginBannerRef} className={styles.chatInputContainer}>
                      <Banner
                        title={`This session is steered from ${channelOrigin}`}
                        description={channelOriginMessage}
                      />
                    </div>
                  ) : (
                    !everySessionReadonly &&
                    (!isAhpTask && hasPendingPlan && pendingPlanContent && lastSession ? (
                      <div className={styles.chatInputContainer}>
                        <PlanApprovalDisplay
                          summary={pendingPlanContent}
                          planContent={pendingPlanContent}
                          actions={CCA_PLAN_ACTIONS}
                          recommendedAction="implement"
                          onAction={action => handlePlanApprove(lastSession)(action, pendingPlanContent)}
                          isTokenBasedBillingEnabled={effectivePayload.useTokenBasedBilling}
                        />
                      </div>
                    ) : (
                      <div
                        className={styles.chatInputContainer}
                        data-unified-loading-owned={continuationEligibility === 'pending' ? '' : undefined}
                        onFocusCapture={() => (chatInputHadFocusRef.current = true)}
                        onBlurCapture={event => {
                          if (!event.currentTarget.contains(event.relatedTarget)) chatInputHadFocusRef.current = false
                        }}
                      >
                        {shouldRenderEstablishedTaskChatInput ? (
                          <>
                            {historyOnly && (
                              <>
                                <ChatContinuationUnavailable onStartNew={startNewConversation} focusOnMount compact />
                                <AhpTaskChatInputActions
                                  repoNwo={repoNwo}
                                  taskId={effectiveTaskId ?? ''}
                                  baseRef=""
                                  headRef=""
                                  onViewDiff={handleViewDiff}
                                />
                              </>
                            )}
                            {activeRootOffline && (
                              <div
                                ref={offlineNoticeRef}
                                tabIndex={-1}
                                role="group"
                                data-testid="offline-environment-notice"
                                aria-label="Environment offline. The environment for this session is offline. Steering will resume when the host reconnects."
                              >
                                <Banner
                                  title="Environment offline"
                                  description="The environment for this session is offline. Steering will resume when the host reconnects."
                                />
                              </div>
                            )}
                            {firstTurnRecovery.error && (
                              <Banner title="First message recovery failed" description={firstTurnRecovery.error} />
                            )}
                            <EstablishedTaskChatInput
                              {...taskChatInputProps}
                              claimActiveClient={!steerableDm}
                              initialSelectedSpace={initialSelectedSpace}
                              directTurnTelemetryContext={directTurnTelemetryContext}
                              resumeErrorEntrypoint={directTurnTelemetrySurface}
                              restoredPrompt={restoredPrompt ?? displayedProvisioningDraft ?? ''}
                              onRestoredPromptChange={onRestoredPromptChange ?? onProvisioningDraftChange}
                              onResumeIntent={
                                currentUserIsSessionOwner &&
                                (rootAwaitingIntent ||
                                  rootReconnectPendingIntent ||
                                  (rootDeferralFlag &&
                                    !taskStartup &&
                                    !suppliedEstablishedSession &&
                                    activeAhpRootSession &&
                                    (managedSandboxConversation ||
                                      existingSandboxRoot ||
                                      (!taskData && !activeRootEligibility.environmentKind))))
                                  ? requestRootConnection
                                  : undefined
                              }
                              onRestoredPromptAccepted={
                                restoredPrompt !== undefined ? onRestoredPromptAccepted : undefined
                              }
                              onModelChange={handleModelChange}
                              onReasoningEffortChange={handleReasoningEffortChange}
                              connectionPending={rootResumeConnectionPending}
                              connectionError={rootResumeConnectionError}
                              connectionFailure={rootConnection.failure}
                              newConversationHref={newConversationHref}
                              connectionProgress={
                                rootConnection.progress ??
                                (suppliedEstablishedSession ? activeRootEligibility.connectionProgress : undefined)
                              }
                              connectionPresentationPhase={
                                rootConnection.presentationPhase ?? suppliedConnectionPresentation.presentationPhase
                              }
                              connectionSlow={rootConnection.slow || suppliedConnectionPresentation.slow}
                              canRetryConnection={
                                currentUserIsSessionOwner &&
                                instructionsReady &&
                                (continuationMetadataError
                                  ? !continuationMetadataRetryPending
                                  : rootMetadataError
                                    ? !rootMetadataRetryPending
                                    : rootConnection.canRetry)
                              }
                              connectionDraftOnly={composerDraftOnly}
                              readOnly={Boolean(pendingTaskStartupPrompt || displayedProvisioningPrompt)}
                              onResume={instructionsReady ? onRootResume : undefined}
                              hidden={historyOnly}
                              loadingPresentation="page"
                            />
                          </>
                        ) : continuationMetadataError && !taskChatInputProps ? (
                          <SessionResumeNotice
                            errorTelemetry={{
                              entrypoint: directTurnTelemetrySurface,
                              taskId: activeAhpRootSession?.task_id ?? effectiveTaskId,
                              sessionId: activeAhpRootSession?.id,
                              environmentId: activeRootEligibility.environmentId ?? taskEnvironmentId,
                            }}
                            pending={false}
                            error={establishedRootError}
                            canRetry={!continuationMetadataRetryPending}
                            onRetry={retryContinuationMetadata}
                          />
                        ) : agentProfile.slots.ChatInput && lastSession && !isAhpTask ? (
                          <agentProfile.slots.ChatInput
                            sessionId={lastSession.id}
                            session={lastSession}
                            task={taskData}
                            onCommandSent={handleCommandSent}
                            cliRemoteNoticeDismissed={cliRemoteNoticeDismissed}
                            remoteControlPolicyEnabled={remoteControlPolicyEnabled}
                            ssoOrgIds={initialAuthToken?.ssoOrgIDs}
                          />
                        ) : taskChatInputProps ? (
                          <TaskChatInputV2 {...taskChatInputProps} draftOnly={!instructionsReady} />
                        ) : null}
                      </div>
                    ))
                  ))}
              </div>
            </div>
          </OptimisticLogsProvider>
        </AgentProfileProvider>
      </TaskChatSessionProvider>
      {contributedInstruction && (
        <AppliedInstructionsDialog
          contributedInstruction={contributedInstruction}
          currentInstructions={currentInstructions}
          onDismiss={closeAppliedInstructions}
          returnFocusRef={appliedInstructionsTriggerRef}
        />
      )}
      {isPersonalInstructionsDialogOpen && (
        <PersonalInstructionsDialog
          onDismiss={closePersonalInstructions}
          returnFocusRef={appliedInstructionsTriggerRef}
          personalInstructions={personalInstructionsEditor?.personalInstructions}
          onPersonalInstructionsChange={personalInstructionsEditor?.updatePersonalInstructions}
        />
      )}
    </TaskActionProviders>
  )

  return (
    <SessionLoadingBoundary
      displayKey={`${effectiveTaskId ?? taskData?.id ?? 'task'}:${urlSessionId ?? ''}`}
      visible
      sources={pageLoadingSources}
    >
      <ConversationAccessContext value={reportConversationRefused}>
        <NewTaskContext value={startedHere ? effectiveTaskId : undefined}>
          <ConversationLockedFocusContext value={registerLockedState}>
            <TimelineVerbosityProvider enabled={showVerbosityControl}>{taskChat}</TimelineVerbosityProvider>
          </ConversationLockedFocusContext>
        </NewTaskContext>
      </ConversationAccessContext>
    </SessionLoadingBoundary>
  )
}
